/**
 * EDM User Standalone
 * @version 8.4.3
 * @author EDM Team
 * @license Proprietary
 * 
 * v8.4.3 변경:
 * - badges-item__medium 컨테이너 자동 감지 및 적용
 * - createPriceElements() 헬퍼 함수로 중복 코드 제거
 * - 배지 크기 클래스 자동 추론 (small vs medium)
 * 
 * v8.4.2 변경:
 * - Sale badge text 색상을 EDM 배지에 적용
 * - CSS 클래스 기반 스타일링으로 일관성 개선
 * - head-styles.liquid에 color_save_badge_great/hot 변수 추가
 * 
 * v8.4.1 변경:
 * - 0% 할인 감지 버그 수정 (hasActualCompareDiscount)
 * 
 * v8.4.0 변경:
 * - Label Priority 로직 개선: regular_first 모드에서 기본 할인 있으면 항상 유지
 * - Sale badge 색상 통합: color_save_badge_* 스키마와 EDM 배지 색상 공유
 * - 할인율별 배지 색상: 20%+, 30%+ 기준으로 구분
 * 
 * v8.3.2 변경:
 * - 중복 초기화 방지 강화 (EDM_USER_INITIALIZED window 플래그)
 * - 테마 에디터 iframe 재로드 시 중복 실행 방지
 */

(function() {
  'use strict';

  /**
   * 🔧 Admin 모드 감지 (Shopify 디자인 모드)
   */
  function detectAdminMode() {
    return window.Shopify?.designMode === true || 
           window.location.search.includes('design_mode=true') ||
           window.location.pathname.includes('/admin/');
  }
  
  /**
   * storageKey 자동 생성 (shop.permanent_domain 없이)
   * 현재 호스트명에서 고유 키 생성
   */
  function generateStorageKey() {
    // theme.liquid에서 전달된 storageKey 우선 사용
    if (window.edmSettings?.config?.cache?.storageKey) {
      return window.edmSettings.config.cache.storageKey;
    }
    // 폴백: 호스트명에서 생성
    const host = window.location.hostname
      .replace(/\.myshopify\.com$/, '')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    return `edm_cache_v6_${host}`;
  }

  /**
   * 🔧 EDM 완전 기본 설정 (settings_schema.json과 완전 동기화)
   * theme.liquid 의존성 없이 완전 독립적으로 동작
   * 
   * 우선순위:
   * 1. window.EDM_THEME_SETTINGS (테마 에디터에서 주입 가능)
   * 2. window.edmSettings (호환성)
   * 3. EDM_DEFAULTS (JS 내장 기본값)
   * 
   * 스키마 ID 매핑 (v8.3.5):
   * - edm_enabled → enabled
   * - edm_debug → config.debug
   * - edm_show_badges → badge.show
   * - edm_show_discount_info → badge.showDiscountInfo
   * - edm_label_* → badge.labelEnabled, badge.labelTemplate, badge.labelPriority
   * - edm_badge_color* → badge.color, badge.colorGreat, badge.colorHot
   * - edm_indicator_* → indicator.*
   */
  const EDM_DEFAULTS = {
    // ═══════════════════════════════════════════════════════════════
    // 일반 설정 (General) - settings_schema.json 완전 동기화
    // ═══════════════════════════════════════════════════════════════
    enabled: true,                        // edm_enabled
    adminMode: detectAdminMode(),         // request.design_mode
    applyToProductCards: true,            // edm_apply_to_product_cards
    applyToSoldOut: true,                // (soldout 상품에 할인 적용)
    soldoutBadgeOpacity: 70,              // edm_soldout_badge_opacity
    useMetafields: false,                 // edm_use_metafields
    showLoading: true,                    // edm_show_loading
    
    // ═══════════════════════════════════════════════════════════════
    // 설정 (Config) - settings_schema.json 완전 동기화
    // ═══════════════════════════════════════════════════════════════
    config: {
      preset: 'default',                  // edm_preset
      debug: false,                       // edm_debug
      cache: {
        expiry: 14400000,                 // edm_cache_ttl (4시간, ms)
        maxSize: 200,                     // edm_cache_max_size
        storageKey: null,                 // 동적 생성
        ttlMemory: 86400000,              // edm_cache_ttl_memory (24시간)
        ttlGist: 86400000,                // edm_cache_ttl_gist (24시간)
        ttlStorefront: 86400000,          // edm_cache_ttl_storefront (24시간)
        scope: 'none'                     // edm_cache_scope: none|all|selected
      },
      features: {
        showLoadingIndicator: true        // edm_indicator_enabled
      },
      performance: {
        batchSize: 4,                     // edm_batch_size
        batchDelay: 0,                    // edm_batch_delay
        apiDelay: 25,                     // edm_api_delay
        throttleDelay: 250                // edm_throttle_delay
      },
      warming: {
        batchSize: 5,                     // edm_warming_batch_size
        maxProducts: 250                  // edm_warming_max_products
      }
    },
    
    // ═══════════════════════════════════════════════════════════════
    // 캐시 워밍 컬렉션 (edm_collection_1~5)
    // ═══════════════════════════════════════════════════════════════
    cacheCollections: {
      collection1: '',                    // edm_collection_1
      collection2: '',                    // edm_collection_2
      collection3: '',                    // edm_collection_3
      collection4: '',                    // edm_collection_4
      collection5: ''                     // edm_collection_5
    },
    
    // ═══════════════════════════════════════════════════════════════
    // 배지 설정 (Badge) - 통합 구조 (themeSettings, variableLabels 통합)
    // settings_schema.json 기본값과 동기화
    // ═══════════════════════════════════════════════════════════════
    badge: {
      // 배지 표시 설정
      show: true,                         // edm_show_badges
      showDiscountInfo: true,             // edm_show_discount_info
      
      // 라벨 설정
      labelEnabled: true,                 // edm_label_enabled
      labelTemplate: '[percent]% off',    // edm_label_template (스키마 기본값 동기화)
      labelPriority: 'regular_first',     // edm_label_priority
      
      // 배지 색상 - Product card color 섹션의 Sale badge 색상과 통합
      color: '#A0F68A',                   // color_save_badge (default)
      colorGreat: '#ff6b35',              // color_save_badge_great (20%+)
      colorHot: '#ff3e3e',                // color_save_badge_hot (30%+)
      textColor: '#ffffff'                // color_save_badge_text
    },
    
    // ═══════════════════════════════════════════════════════════════
    // 테마 기본 배지 설정 (레거시 호환)
    // ═══════════════════════════════════════════════════════════════
    themeSettings: {
      badgeSaleType: 'percent',           // badge_sale_type
      saleText: 'Sale',                   // sale_text
      salePercentText: '[percent]% off'   // sale_percent_text
    },
    
    // ═══════════════════════════════════════════════════════════════
    // Gist 설정 (L4 Cache) - settings_schema.json 완전 동기화
    // ═══════════════════════════════════════════════════════════════
    gist: {
      enabled: true,                      // edm_gist_enabled
      gistId: '8b8a738ecdc34dc87e5580b6b851c7f6',  // edm_gist_id
      filename: 'edm-discount-cache.json', // edm_gist_filename
      token: ''                           // edm_gist_token (Admin에서만 사용)
    },
    
    // ═══════════════════════════════════════════════════════════════
    // 인디케이터 설정 (Loading Indicator) - settings_schema.json 완전 동기화
    // ═══════════════════════════════════════════════════════════════
    indicator: {
      // 기본 설정
      enabled: true,                      // edm_indicator_enabled
      showToUsers: true,                  // edm_indicator_show_users
      design: 'modern',                   // edm_indicator_design
      animation: 'spring',                // edm_indicator_animation
      animationMobile: 'default',         // edm_indicator_animation_mobile
      position: 'top-right',              // edm_indicator_position
      
      // 텍스트 설정 (기본값 제거)
      text: '',                           // edm_indicator_text
      textProgress: '',                   // edm_indicator_text_progress
      textComplete: '',                   // edm_indicator_text_complete
      subText: '',                        // edm_indicator_sub_text
      
      // 색상 설정
      bgColor: '#F44336',                 // edm_indicator_bg_color
      textColor: '#ffffff',               // edm_indicator_text_color
      accentColor: '#FFFFFF',             // edm_indicator_accent_color
      
      // 기타 설정
      opacity: 90,                        // edm_indicator_opacity (0-100)
      showSpinner: true,                  // edm_indicator_spinner
      showProgress: true,                 // edm_indicator_progress
      autoHide: 2000,                     // edm_indicator_autohide (ms)
      persistInAdmin: false,              // edm_indicator_persist_admin
      offsetY: 80,                        // edm_indicator_offset_y (px)
      offsetYMobile: 80,                  // edm_indicator_offset_y_mobile (px)
      offsetX: 20                         // edm_indicator_offset_x (px)
    }
  };

  /**
   * 설정 병합 유틸리티 (깊은 병합)
   */
  function deepMerge(target, source) {
    const output = { ...target };
    if (isObject(target) && isObject(source)) {
      Object.keys(source).forEach(key => {
        if (isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = deepMerge(target[key], source[key]);
          }
        } else if (source[key] !== undefined) {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  }

  function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  /**
   * 🔧 테마 에디터 설정 로드 (window.edmSettings 또는 window.EDM_THEME_SETTINGS)
   * theme.liquid에서 선택적으로 주입 가능
   * 없어도 JS 기본값으로 완전 동작
   * 
   * 스키마 변수 매핑 (settings_schema.json → EDM_SETTINGS):
   * - edm_enabled → enabled
   * - edm_debug → config.debug
   * - edm_preset → config.preset
   * - edm_cache_ttl → config.cache.expiry (시간 → ms 변환)
   * - edm_cache_max_size → config.cache.maxSize
   * - edm_cache_scope → config.cache.scope
   * - edm_batch_size → config.performance.batchSize
   * - edm_warming_batch_size → config.warming.batchSize
   * - edm_indicator_* → indicator.*
   * - edm_gist_* → gist.*
   */
  function loadThemeEditorSettings() {
    // 테마 에디터 값 우선 적용 (실시간 변경 반영)
    const themeSettings = window.theme?.settings || {};
    const edmSettings = window.edmSettings || window.EDM_THEME_SETTINGS || {};
    
    // 테마 에디터 값 우선, edmSettings를 fallback으로 사용
    let ts = themeSettings;
    if (Object.keys(ts).length === 0) {
      ts = edmSettings;
    }
    
    if (Object.keys(ts).length === 0) return {};
    
    // 직접 매핑된 설정들 (theme.liquid에서 이미 구조화된 경우)
    if (ts.config || ts.indicator || ts.gist) {
      return ts; // 이미 구조화된 설정 그대로 반환
    }
    
    // 스키마 변수에서 직접 매핑 (평탄한 구조인 경우)
    return {
      enabled: ts.edm_enabled,
      adminMode: ts.adminMode,
      applyToProductCards: ts.edm_apply_to_product_cards ?? ts.edm_apply_to_cards,
      applyToSoldOut: ts.edm_apply_to_soldout,
      soldoutBadgeOpacity: ts.edm_soldout_badge_opacity,
      useMetafields: ts.edm_use_metafields,
      showLoading: ts.edm_show_loading,
      
      config: {
        preset: ts.edm_preset,
        debug: ts.edm_debug,
        cache: {
          expiry: ts.edm_cache_ttl ? ts.edm_cache_ttl * 3600000 : undefined,
          maxSize: ts.edm_cache_max_size,
          ttlMemory: ts.edm_cache_ttl_memory,
          ttlGist: ts.edm_cache_ttl_gist,
          ttlStorefront: ts.edm_cache_ttl_storefront,
          scope: ts.edm_cache_scope
        },
        performance: {
          batchSize: ts.edm_batch_size,
          batchDelay: ts.edm_batch_delay,
          apiDelay: ts.edm_api_delay,
          throttleDelay: ts.edm_throttle_delay
        },
        warming: {
          batchSize: ts.edm_warming_batch_size,
          maxProducts: ts.edm_warming_max_products
        }
      },
      
      cacheCollections: {
        collection1: ts.edm_collection_1,
        collection2: ts.edm_collection_2,
        collection3: ts.edm_collection_3,
        collection4: ts.edm_collection_4,
        collection5: ts.edm_collection_5
      },
      
      themeSettings: {
        badgeSaleType: ts.badge_sale_type,
        saleText: ts.sale_text,
        salePercentText: ts.sale_percent_text
      },
      
      badge: {
        show: ts.edm_show_badges ?? ts.badge?.show,
        showDiscountInfo: ts.edm_show_discount_info ?? ts.badge?.showDiscountInfo,
        labelEnabled: ts.edm_label_enabled ?? ts.badge?.labelEnabled,
        labelTemplate: ts.edm_label_template ?? ts.badge?.labelTemplate,
        labelPriority: ts.edm_label_priority ?? ts.badge?.labelPriority,
        color: ts.edm_badge_color ?? ts.badge?.color,
        colorGreat: ts.edm_badge_color_great ?? ts.badge?.colorGreat,
        colorHot: ts.edm_badge_color_hot ?? ts.badge?.colorHot
      },
      
      gist: {
        enabled: ts.edm_gist_enabled,
        gistId: ts.edm_gist_id,
        filename: ts.edm_gist_filename,
        token: ts.edm_gist_token
      },
      
      indicator: {
        enabled: ts.edm_indicator_enabled,
        showToUsers: ts.edm_indicator_show_users,
        design: ts.edm_indicator_design,
        animation: ts.edm_indicator_animation,
        animationMobile: ts.edm_indicator_animation_mobile ?? ts.indicator?.animationMobile,
        position: ts.edm_indicator_position,
        text: ts.edm_indicator_text,
        textProgress: ts.edm_indicator_text_progress,
        textComplete: ts.edm_indicator_text_complete,
        subText: ts.edm_indicator_sub_text,
        bgColor: ts.edm_indicator_bg_color,
        textColor: ts.edm_indicator_text_color,
        accentColor: ts.edm_indicator_accent_color,
        opacity: ts.edm_indicator_opacity,
        showSpinner: ts.edm_indicator_spinner,
        showProgress: ts.edm_indicator_progress,
        autoHide: ts.edm_indicator_autohide,
        persistInAdmin: ts.edm_indicator_persist_admin,
        offsetY: ts.edm_indicator_offset_y,
        offsetYMobile: ts.edm_indicator_offset_y_mobile,
        offsetX: ts.edm_indicator_offset_x
      }
    };
  }

  /**
   * 🎨 EDM CSS 변수 자동 주입 (v8.2 - 완전 독립)
   * theme.liquid 없이도 JS에서 직접 CSS 변수 생성
   * 
   * 스키마 매핑:
   * - edm_badge_color → --edm-badge-color
   * - edm_badge_color_great → --edm-badge-color-great
   * - edm_badge_color_hot → --edm-badge-color-hot
   * - edm_indicator_bg_color → --edm-indicator-bg
   * - edm_indicator_text_color → --edm-indicator-text
   * - edm_indicator_accent_color → --edm-indicator-accent
   * - edm_indicator_opacity → --edm-indicator-opacity
   * - edm_indicator_offset_y → --edm-indicator-offset-y
   * - edm_indicator_offset_x → --edm-indicator-offset-x
   */
  function injectEDMCSSVariables() {
    // 이미 주입된 경우 스킵 (theme.liquid 또는 JS에서)
    if (document.querySelector('#edm-css-variables') || 
        document.querySelector('#edm-schema-css-variables') ||
        document.querySelector('#edm-injected-css-variables')) {
      return;
    }
    
    // EDM_SETTINGS에서 값 가져오기 (통합된 badge 구조 사용)
    const badge = EDM_SETTINGS.badge;
    const ind = EDM_SETTINGS.indicator;
    
    const style = document.createElement('style');
    style.id = 'edm-css-variables';
    style.textContent = `
      :root {
        /* ═══════════════════════════════════════════════════════════ */
        /* EDM 배지 색상 (스키마: edm_badge_color*) */
        /* ═══════════════════════════════════════════════════════════ */
        --edm-badge-color: ${badge.color || '#A0F68A'};
        --edm-badge-color-great: ${badge.colorGreat || '#ff6b35'};
        --edm-badge-color-hot: ${badge.colorHot || '#ff3e3e'};
        --edm-badge-text-color: ${badge.textColor || '#ffffff'};
        --edm-badge-radius: 4px;
        --edm-badge-padding: 4px 10px;
        --edm-badge-font-size: calc(12px * var(--font-body-scale, 1));
        --edm-transition-duration: 0.2s;
        
        /* ═══════════════════════════════════════════════════════════ */
        /* EDM 인디케이터 (스키마: edm_indicator_*) */
        /* ═══════════════════════════════════════════════════════════ */
        --edm-indicator-bg: ${ind.bgColor || '#F44336'};
        --edm-indicator-text: ${ind.textColor || '#ffffff'};
        --edm-indicator-accent: ${ind.accentColor || '#FFFFFF'};
        --edm-indicator-opacity: ${(ind.opacity ?? 90) / 100};
        --edm-indicator-offset-y: ${ind.offsetY ?? 80}px;
        --edm-indicator-offset-y-mobile: ${ind.offsetYMobile ?? 80}px;
        --edm-indicator-offset-x: ${ind.offsetX ?? 20}px;
        --edm-indicator-radius: var(--radius, 8px);
        --edm-indicator-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08);
        
        /* ═══════════════════════════════════════════════════════════ */
        /* 품절 상품 투명도 */
        /* ═══════════════════════════════════════════════════════════ */
        --edm-soldout-opacity: ${(EDM_SETTINGS.soldoutBadgeOpacity || 70) / 100};
      }
    `;
    document.head.appendChild(style);
    
    if (window.EDM_DEBUG) {
      console.log('[EDM v8.2] CSS variables injected from JS:', {
        badge: { color: badge.color, colorGreat: badge.colorGreat, colorHot: badge.colorHot },
        indicator: { bg: ind.bgColor, text: ind.textColor, accent: ind.accentColor, opacity: ind.opacity }
      });
    }
  }

  /**
   * 🔄 CSS 변수에서 스키마 설정 런타임 추론 (v8.0)
   * 기존 CSS 변수가 있으면 사용, 없으면 기본값 사용
   */
  function inferSchemaSettings() {
    const computed = {};
    
    // CSS 변수에서 색상 설정 추론
    const rootStyles = getComputedStyle(document.documentElement);
    const getCSSVar = (name, fallback) => {
      const val = rootStyles.getPropertyValue(name).trim();
      return val || fallback;
    };
    
    // 배지 색상 (CSS 변수에서 읽기) - 새로운 badge 구조 사용
    computed.badge = {
      color: getCSSVar('--edm-badge-color', EDM_DEFAULTS.badge.color),
      colorGreat: getCSSVar('--edm-badge-color-great', EDM_DEFAULTS.badge.colorGreat),
      colorHot: getCSSVar('--edm-badge-color-hot', EDM_DEFAULTS.badge.colorHot)
    };
    
    // 인디케이터 설정 (CSS 변수에서 읽기)
    computed.indicator = {
      bgColor: getCSSVar('--edm-indicator-bg', EDM_DEFAULTS.indicator.bgColor),
      textColor: getCSSVar('--edm-indicator-text', EDM_DEFAULTS.indicator.textColor),
      accentColor: getCSSVar('--edm-indicator-accent', EDM_DEFAULTS.indicator.accentColor)
    };
    
    // 오프셋 값 추론 (픽셀 단위)
    const offsetY = getCSSVar('--edm-indicator-offset-y', '80px');
    const offsetYMobile = getCSSVar('--edm-indicator-offset-y-mobile', '80px');
    const offsetX = getCSSVar('--edm-indicator-offset-x', '20px');
    computed.indicator.offsetY = parseInt(offsetY, 10) || 80;
    computed.indicator.offsetYMobile = parseInt(offsetYMobile, 10) || 80;
    computed.indicator.offsetX = parseInt(offsetX, 10) || 20;
    
    // 불투명도 추론
    const opacity = getCSSVar('--edm-indicator-opacity', '0.9');
    computed.indicator.opacity = Math.round(parseFloat(opacity) * 100) || 90;
    
    return computed;
  }

  /**
   * 배지 설정 로드 (새로운 통합 badge 구조)
   * theme.liquid에서 window.edmSettings.badge로 전달됨
   * @returns {Object} badge 설정 객체
   */
  function loadBadgeSettings() {
    const defaultBadge = EDM_DEFAULTS.badge;
    const themeBadge = window.edmSettings?.badge;
    
    // 이전 variableLabels/themeSettings 호환
    const themeLabels = window.edmSettings?.variableLabels;
    const themeSettings = window.edmSettings?.themeSettings || {};
    
    if (!themeBadge && !themeLabels) {
      return defaultBadge;
    }
    
    // 새 badge 구조 (themeBadge > themeLabels > themeSettings > defaults)
    return {
      show: themeBadge?.show ?? themeSettings.edm_show_badges ?? defaultBadge.show,
      showDiscountInfo: themeBadge?.showDiscountInfo ?? themeSettings.edm_show_discount_info ?? defaultBadge.showDiscountInfo,
      labelEnabled: themeBadge?.labelEnabled ?? themeLabels?.enabled ?? themeSettings.edm_label_enabled ?? defaultBadge.labelEnabled,
      labelTemplate: themeBadge?.labelTemplate || themeLabels?.template || themeSettings.edm_label_template || defaultBadge.labelTemplate,
      labelPriority: themeBadge?.labelPriority || themeLabels?.priority || themeSettings.edm_label_priority || defaultBadge.labelPriority,
      color: themeBadge?.color || themeLabels?.color || themeSettings.edm_badge_color || defaultBadge.color,
      colorGreat: themeBadge?.colorGreat || themeLabels?.colorGreat || themeSettings.edm_badge_color_great || defaultBadge.colorGreat,
      colorHot: themeBadge?.colorHot || themeLabels?.colorHot || themeSettings.edm_badge_color_hot || defaultBadge.colorHot
    };
  }

  /**
   * 🔧 설정 초기화 (v8.2 완전 독립 아키텍처)
   * 
   * 우선순위:
   * 1. EDM_DEFAULTS (JS 내장 기본값)
   * 2. window.edmSettings (호환성)
   * 3. window.EDM_THEME_SETTINGS (테마 에디터에서 주입)
   * 4. inferSchemaSettings() (CSS 변수에서 런타임 추론)
   */
  let EDM_SETTINGS = deepMerge(
    deepMerge(EDM_DEFAULTS, window.edmSettings || {}),
    loadThemeEditorSettings()
  );
  
  // storageKey 동적 생성
  EDM_SETTINGS.config.cache.storageKey = generateStorageKey();
  
  // badge 설정 로드 (통합 구조)
  EDM_SETTINGS.badge = loadBadgeSettings();

  // DOM 준비 후 CSS 변수에서 추론한 설정 병합
  function finalizeSettings() {
    if (document.readyState !== 'loading') {
      const inferred = inferSchemaSettings();
      EDM_SETTINGS = deepMerge(EDM_SETTINGS, inferred);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // 🔧 상수 정의
  // ═══════════════════════════════════════════════════════════════════
  
  const EDM_CONSTANTS = {
    VERSION: '8.4.3',
    GIST_CACHE_EXPIRY_MS: EDM_SETTINGS.config.cache.ttlGist || 86400000,
    OBSERVER_THROTTLE_MS: 300,
    OBSERVER_IDLE_TIMEOUT_MS: 2000,
    STORAGE_KEY: EDM_SETTINGS.config.cache.storageKey,
    DEBUG: window.EDM_DEBUG || EDM_SETTINGS.config.debug || false,
    VERBOSE: window.EDM_VERBOSE_DEBUG || false
  };

  // 선택자 정의 (Wokiee 테마 호환 v7.0 - 상수화)
  const EDM_SELECTORS = {
    // 하드코딩된 상수 (반복 사용되므로 getter 제거)
    CARDS: '.product-card, .product-card-small, .lookbook__product-card',
    PRICE_CONTAINER: '[data-edm-price-container], .price__container, .price',
    IMAGE_CONTAINER: '.product-card__image-container, .product-card-small__image-container, .lookbook__product-card__image, .product-card__image__container, .badges__container',
    
    // Predictive Search
    predictiveSearch: {
      container: '.predictive-search__results-list, #predictive-search-results, predictive-search .predictive-search__results',
      item: '.predictive-search__list-item, .predictive-search__item__product',
      price: '[data-edm-price-container], .predictive-search__price'
    },
    // Sidebar
    sidebar: {
      item: '.sidebar-product-item, .sidebar-recently-viewed__item',
      price: '[data-edm-price-container], .sidebar-product-item__price'
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // 🛠️ 유틸리티 함수
  // ═══════════════════════════════════════════════════════════════════

  /**
   * 가격 포맷팅 헬퍼
   */
  /**
   * Liquid 필터 호환 가격 포맷터
   * Liquid 필터명(| money, | money_with_currency)을 키로 사용
   */
  const PriceFormatter = {
    /**
     * | money 필터와 동일: 통화 기호 없는 가격 포맷팅
     * @param {number} cents - 센트 단위 금액
     * @returns {string} 포맷팅된 가격 (예: $10.00)
     */
    money(cents) {
      if (!cents && cents !== 0) return '0';
      // Shopify.formatMoney는 Liquid | money와 동일
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      // 기본 포맷팅 (fallback)
      const currency = window.Shopify?.currency?.active || 'USD';
      const symbols = { 'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'KRW': '₩' };
      const symbol = symbols[currency] || '$';
      return symbol + (cents / 100).toFixed(2);
    },

    /**
     * | money_with_currency 필터와 동일: 통화 코드 포함 가격 포맷팅
     * @param {number} cents - 센트 단위 금액
     * @returns {string} 포맷팅된 가격 (예: USD $10.00)
     */
    money_with_currency(cents) {
      if (!cents && cents !== 0) return '0';
      // 테마의 Currency.formatMoney는 Liquid | money_with_currency와 유사
      if (window.theme?.Currency?.formatMoney) {
        return window.theme.Currency.formatMoney(cents);
      }
      // Shopify.formatMoney fallback (통화 코드 포함 버전)
      if (window.Shopify?.formatMoney) {
        const formatted = window.Shopify.formatMoney(cents);
        const currency = window.Shopify?.currency?.active || 'USD';
        return `${currency} ${formatted}`;
      }
      // 기본 포맷팅
      const currency = window.Shopify?.currency?.active || 'USD';
      const symbols = { 'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'KRW': '₩' };
      const symbol = symbols[currency] || '$';
      return `${currency} ${symbol}${(cents / 100).toFixed(2)}`;
    },

    /**
     * Liquid 필터명으로 가격 포맷팅 (표준화 인터페이스)
     * @param {number} cents - 센트 단위 금액
     * @param {string} filter - Liquid 필터명 ('money' | 'money_with_currency')
     * @returns {string} 포맷팅된 가격
     */
    format(cents, filter = 'money') {
      if (this[filter]) {
        return this[filter](cents);
      }
      // 기본값: money 필터
      return this.money(cents);
    },

    /**
     * 기존 호환성 유지: formatPrice → money 필터
     * @deprecated Use PriceFormatter.money() or PriceFormatter.format(cents, 'money')
     */
    formatPrice(cents) {
      return this.money(cents);
    },

    /**
     * 가격 텍스트 파싱 (숫자 추출)
     * @param {string} priceText - 가격 텍스트
     * @returns {number} 파싱된 가격 (센트 단위)
     */
    parsePrice(priceText) {
      if (!priceText) return 0;
      // 숫자만 추출 (통화 기호, 공백, 콤마 제거)
      const cleaned = priceText.toString().replace(/[^\d.,-]/g, '').replace(/,/g, '');
      const num = parseFloat(cleaned);
      if (isNaN(num)) return 0;
      // 소수점 이하가 없거나 2자리 이하면 달러 단위로 간주하고 센트로 변환
      return (num < 100 && num === Math.floor(num)) ? num * 100 : num;
    },

    /**
     * 금액을 센트 단위로 변환
     * @param {number|string} amount - 입력 금액
     * @returns {number} 센트 단위 금액
     */
    toCents(amount) {
      if (!amount && amount !== 0) return 0;
      const num = typeof amount === 'string' ? parseFloat(amount) : amount;
      if (isNaN(num)) return 0;
      // 1000 이상이거나 정수형이고 100 초과면 이미 센트 단위로 간주
      return (num >= 1000 || (Number.isInteger(num) && num > 100)) ? num : num * 100;
    }
  };

  /**
   * Variant ID 추출 헬퍼
   * @param {HTMLElement} element - DOM 요소
   * @returns {number|null} Variant ID
   */
  function extractVariantId(element) {
    if (!element || !(element instanceof HTMLElement)) return null;
    
    // 캐시된 값 확인
    if (element._edmVariantId) return element._edmVariantId;
    
    let variantId = null;
    
    // 1. 요소 자체의 data-variant-id
    variantId = element.dataset?.variantId;
    
    // 2. 자식 중 [data-variant-id]
    if (!variantId) {
      const variantEl = element.querySelector('[data-variant-id]');
      variantId = variantEl?.dataset?.variantId;
    }
    
    // 3. input[name="id"]
    if (!variantId) {
      const input = element.querySelector('input[name="id"]');
      variantId = input?.value;
    }
    
    // 4. URL 파라미터
    if (!variantId) {
      const link = element.querySelector('a[href*="variant="]');
      if (link) {
        const match = link.href.match(/[?&]variant=(\d+)/);
        variantId = match?.[1];
      }
    }
    
    if (variantId) {
      variantId = Number(variantId);
      if (!isNaN(variantId)) {
        element._edmVariantId = variantId;
        return variantId;
      }
    }
    
    return null;
  }

  /**
   * 템플릿 파싱 헬퍼 (라벨 템플릿용)
   * @param {string} template - 템플릿 문자열
   * @param {Object} data - 치환 데이터
   * @returns {string} 파싱된 문자열
   */
  function parseTemplate(template, data) {
    if (!template || typeof template !== 'string') return '';
    
    return template
      .replace(/\[percent\]/g, data.percent || 0)
      .replace(/\[amount\]/g, data.amount || 0)
      .replace(/\[original\]/g, data.originalPrice || 0)
      .replace(/\[discounted\]/g, data.discountedPrice || 0);
  }

  /**
   * 이미지 컨테이너 찾기 헬퍼
   */
  function findImageContainer(card, selectors) {
    const selectorArray = Array.isArray(selectors) ? selectors : [selectors];
    
    for (const selector of selectorArray) {
      const container = card.querySelector(selector);
      if (container) return container;
    }
    
    // card 자체가 컨테이너인지 확인
    for (const selector of selectorArray) {
      const className = selector.replace(/^\./, '');
      if (card.classList?.contains(className)) return card;
    }
    
    // product-card 내에서 찾기
    const productCard = card.querySelector('.product-card') || card.closest('.product-card');
    if (productCard) {
      for (const selector of selectorArray) {
        const container = productCard.querySelector(selector);
        if (container) return container;
      }
    }
    
    return null;
  }

  /**
   * Handle로 variant ID 찾기
   */
  function findVariantIdByHandle(element, cache) {
    const handle = element.dataset?.productHandle || 
                   element.querySelector('a[href*="/products/"]')?.href?.match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle || !cache) return null;
    
    for (const [vid, data] of cache.memory.entries()) {
      if (data.handle === handle || data.productHandle === handle) {
        return vid;
      }
    }
    return null;
  }

  /**
   * 할인 가격 HTML 생성 (통합 버전 v7.1)
   * CSS는 component-edm.css에서 관리
   * @param {Object} discount - 할인 데이터
   * @param {string} type - 템플릿 타입
   * @param {Function} formatPrice - 가격 포맷 함수 (선택사항)
   * @param {string} sizeClass - 사이즈 클래스 (기본: h5)
   */
  function createDiscountPriceHTML(discount, type, formatPrice = null, sizeClass = 'h5') {
    // formatPrice 함수가 제공되지 않으면 기본 PriceFormatter 사용
    const priceFormatter = formatPrice || PriceFormatter.formatPrice.bind(PriceFormatter);

    const originalPrice = discount.originalPrice || discount.discountedPrice || discount.finalPrice || 0;
    const discountedPrice = discount.discountedPrice || discount.finalPrice || 0;
    const originalFormatted = priceFormatter(originalPrice);
    const discountedFormatted = priceFormatter(discountedPrice);

    // CSS 변수 기반 색상 (인라인 스타일 최소화)
    const saleColor = 'var(--color-sale, var(--color-accent, #e53935))';

    const templates = {
      // 상품 카드용 (component-edm.css 스타일 적용)
      product: `
        <div class="price__regular body1 edm-price-compare"><s>${originalFormatted}</s></div>
        <div class="price__sale ${sizeClass} mt1 edm-price-sale">${discountedFormatted}</div>`,
      // 검색 결과용
      search: `
        <span class="edm-search-compare">${originalFormatted}</span>
        <span class="edm-search-sale">${discountedFormatted}</span>`,
      // 사이드바용
      sidebar: `
        <span class="price price--on-sale">
          <span class="price-item price-item--regular edm-price-compare">${originalFormatted}</span>
          <span class="price-item price-item--sale edm-price-sale">${discountedFormatted}</span>
        </span>`,
      // 팝업용
      popup: `
        <div class="price__regular body1 edm-price-compare"><s>${originalFormatted}</s></div>
        <div class="price__sale ${sizeClass} mt1 edm-price-sale">${discountedFormatted}</div>`,
      // 알림용
      notification: `
        <span class="edm-notification-discounted">${discountedFormatted}</span>`,
      // 장바구니용
      cart: `
        <span class="edm-cart-original">${originalFormatted}</span>
        <span class="edm-cart-discounted">${discountedFormatted}</span>`
    };

    return templates[type] || templates.product;
  }

  /**
   * 배경색에 따른 대비 색상 계산
   */
  function getContrastColor(hexColor) {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return yiq >= 128 ? '#000000' : '#ffffff';
  }

  /**
   * EDM 적용 상태 확인 헬퍼
   */
  function isElementAlreadyApplied(element) {
    return element?.dataset?.edmApplied === 'true';
  }

  /**
   * EDM 적용 상태 설정 헬퍼
   */
  function markElementAsApplied(element) {
    if (element) {
      element.dataset.edmApplied = 'true';
    }
  }

  /**
   * EDM 적용 상태 설정 (요소와 컨테이너 모두)
   */
  function markElementsAsApplied(priceElement, containerElement) {
    markElementAsApplied(priceElement);
    markElementAsApplied(containerElement);
  }

  /**
   * 가격 표시 헬퍼 - 정가와 할인가를 동시에 설정
   */
  function setPriceDisplay(regularDiv, saleDiv, discount, finalPrice) {
    if (regularDiv) {
      regularDiv.innerHTML = `<s data-edm-compare-price>${PriceFormatter.money(discount.originalPrice)}</s>`;
    }
    if (saleDiv) {
      saleDiv.textContent = PriceFormatter.money(finalPrice);
    }
  }

  /**
   * 가격 요소 생성 헬퍼 (v8.4.3 - 중복 코드 제거)
   * @param {string} priceSizeClass - 가격 사이즈 클래스 (h1-h6)
   * @param {number} originalPrice - 원래 가격 (센트)
   * @param {number} finalPrice - 최종 가격 (센트)
   * @returns {{regularDiv: HTMLElement, saleDiv: HTMLElement}} 생성된 요소들
   */
  function createPriceElements(priceSizeClass, originalPrice, finalPrice) {
    const regularDiv = document.createElement('div');
    regularDiv.className = 'price__regular body1 edm-price-compare';
    regularDiv.setAttribute('data-edm-compare-wrapper', '');
    regularDiv.innerHTML = `<s data-edm-compare-price>${PriceFormatter.money(originalPrice)}</s>`;
    
    const saleDiv = document.createElement('div');
    saleDiv.className = `price__sale ${priceSizeClass} mt1 edm-price-sale`;
    saleDiv.setAttribute('data-edm-sale-price', '');
    saleDiv.textContent = PriceFormatter.money(finalPrice);
    
    return { regularDiv, saleDiv };
  }

  /**
   * 할인 데이터 조회 및 적용 헬퍼
   */
  function getDiscountAndApply(variantId, applyCallback) {
    const discount = cache.get(variantId);
    if (discount?.hasDiscount) {
      return applyCallback(discount);
    }
    return false;
  }

  /**
   * 이벤트 리스너 일괄 등록 헬퍼
   */
  function addEventListeners(eventMap) {
    Object.entries(eventMap).forEach(([event, handler]) => {
      document.addEventListener(event, handler);
    });
  }

  /**
   * 컨테이너에 할인 적용하는 이벤트 핸들러 생성
   */
  function createContainerHandler(applyFunction) {
    return (e) => {
      const container = e.detail?.container || document;
      applyFunction(container);
    };
  }

  /**
   * 🔧 기본 할인 정보 캐시 (카드별 한 번만 조회)
   * WeakMap을 사용하여 메모리 누수 방지
   */
  const regularDiscountCache = new WeakMap();

  /**
   * 기본 할인 정보 구조체
   * @typedef {Object} RegularDiscountInfo
   * @property {boolean} hasDiscount - 기본 할인 여부
   * @property {number} percent - 할인율 (%)
   * @property {number} comparePrice - 비교 가격 (원래 가격)
   * @property {number} salePrice - 판매 가격
   * @property {string} source - 데이터 소스 ('gist'|'attr'|'class')
   */

  /**
   * 기본 할인 정보 가져오기 (캐시 직접 사용 - 계산 로직 없음)
   * 
   * v8.2.2: DOM 계산 완전 제거, 캐시/속성 데이터만 사용
   * 데이터 소스 우선순위:
   * 1. Gist 캐시 (discount.regularDiscountPercent)
   * 2. data 속성 (price.liquid에서 미리 계산)
   * 3. CSS 클래스 (has-compare-discount)
   * 
   * @param {HTMLElement} card - 상품 카드 또는 priceContainer
   * @param {Object} [discount] - Gist 캐시에서 가져온 할인 데이터
   * @returns {RegularDiscountInfo} 기본 할인 정보
   */
  function getRegularDiscountInfo(card, discount = null) {
    // 캐시에서 반환
    if (regularDiscountCache.has(card)) {
      return regularDiscountCache.get(card);
    }

    const result = {
      hasDiscount: false,
      percent: 0,
      comparePrice: 0,
      salePrice: 0,
      source: 'none'
    };

    // 1순위: Gist 캐시에서 기본 할인 정보 직접 사용
    if (discount?.regularDiscountPercent !== undefined) {
      result.hasDiscount = discount.hasRegularDiscount ?? (discount.regularDiscountPercent > 0);
      result.percent = discount.regularDiscountPercent;
      result.comparePrice = discount.compareAtPrice || 0;
      result.salePrice = discount.originalPrice || 0;
      result.source = 'gist';
      regularDiscountCache.set(card, result);
      return result;
    }

    // priceContainer 찾기
    const priceContainer = card.matches?.('[data-edm-price-container], .price, .price__container')
      ? card
      : card.querySelector('[data-edm-price-container], .price__container, .price');
    
    if (!priceContainer) {
      regularDiscountCache.set(card, result);
      return result;
    }

    // 2순위: data 속성에서 미리 계산된 값 사용 (price.liquid)
    const comparePercentAttr = priceContainer.dataset?.compareDiscountPercent;
    if (comparePercentAttr) {
      result.hasDiscount = priceContainer.dataset.hasCompareDiscount === 'true';
      result.percent = parseInt(comparePercentAttr, 10) || 0;
      result.comparePrice = parseFloat(priceContainer.dataset.compareAtPrice) || 0;
      result.salePrice = parseFloat(priceContainer.dataset.salePrice || priceContainer.dataset.edmOriginalPrice) || 0;
      result.source = 'attr';
      regularDiscountCache.set(card, result);
      return result;
    }

    // 3순위: CSS 클래스로 할인 존재 여부만 확인 (할인율은 0)
    if (priceContainer.classList?.contains('has-compare-discount') ||
        priceContainer.dataset?.hasCompareDiscount === 'true') {
      result.hasDiscount = true;
      result.source = 'class';
    }

    regularDiscountCache.set(card, result);
    return result;
  }

  /**
   * 기본 할인율 헬퍼 (하위 호환성)
   * @param {HTMLElement} card - 상품 카드
   * @param {Object} [discount] - Gist 캐시 데이터
   * @returns {number} 기본 할인율 (%)
   */
  function getRegularDiscountPercent(card, discount = null) {
    return getRegularDiscountInfo(card, discount).percent;
  }

  // ═══════════════════════════════════════════════════════════════════
  // 🗄️ 캐시 시스템 (L1-L4)
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Gist 캐시 관리자 (L4) - 읽기 전용
   * v8.3.0: 세션 스토리지 기반 캐싱으로 재설계
   */
  class GistCacheReader {
    // 전역 상태 (여러 인스턴스 간 공유)
    static _sessionGistData = null;
    static _sessionGistExpiry = 0;
    static _fetchPromise = null;
    static _initialized = false;
    
    // 세션 스토리지 키
    static GIST_SESSION_KEY = 'edm_gist_data_v8';
    static GIST_EXPIRY_KEY = 'edm_gist_expiry_v8';
    
    constructor() {
      const gistConfig = EDM_SETTINGS.gist;
      this.enabled = gistConfig.enabled ?? false;
      this.gistId = gistConfig.gistId;
      this.filename = gistConfig.filename;
      this.rawUrl = `https://gist.githubusercontent.com/atznet/${this.gistId}/raw/${this.filename}`;
      
      // v8.3.5: TTL을 config.cache에서 가져오기 (기본 4시간)
      this.GIST_CACHE_TTL = Math.max(
        EDM_SETTINGS.config.cache.ttlGist || 14400000,
        14400000 // 최소 4시간
      );
      
      this.stats = {
        hits: 0,
        misses: 0,
        errors: 0,
        fetches: 0
      };
      
      // 세션 스토리지에서 복원 (최초 한 번만)
      if (!GistCacheReader._initialized) {
        this._restoreFromSession();
        GistCacheReader._initialized = true;
      }
    }
    
    /**
     * v8.3.0: 세션 스토리지에서 Gist 데이터 복원
     */
    _restoreFromSession() {
      if (GistCacheReader._sessionGistData) return; // 이미 메모리에 있음
      
      try {
        const stored = sessionStorage.getItem(GistCacheReader.GIST_SESSION_KEY);
        const expiry = sessionStorage.getItem(GistCacheReader.GIST_EXPIRY_KEY);
        
        if (stored && expiry) {
          const expiryTime = parseInt(expiry, 10);
          if (Date.now() < expiryTime) {
            GistCacheReader._sessionGistData = JSON.parse(stored);
            GistCacheReader._sessionGistExpiry = expiryTime;
            
            if (EDM_CONSTANTS.DEBUG) {
              const count = Object.keys(GistCacheReader._sessionGistData?.discounts || {}).length;
              console.log(`[EDM-User] Gist restored from session: ${count} items`);
            }
          }
        }
      } catch (e) {
        // 세션 스토리지 접근 실패 (쿼터 초과 등)
      }
    }
    
    /**
     * v8.3.0: Gist 데이터를 세션 스토리지에 저장
     */
    _saveToSession(data, expiry) {
      try {
        sessionStorage.setItem(GistCacheReader.GIST_SESSION_KEY, JSON.stringify(data));
        sessionStorage.setItem(GistCacheReader.GIST_EXPIRY_KEY, String(expiry));
      } catch (e) {
        // 세션 스토리지 쿼터 초과 시 무시
      }
    }

    /**
     * Gist에서 캐시 조회
     */
    async get(variantId) {
      if (!this.enabled) return null;
      
      try {
        const gistData = await this.loadGistData();
        if (!gistData?.discounts) {
          this.stats.misses++;
          return null;
        }
        
        const cached = gistData.discounts[String(variantId)];
        if (cached?.data) {
          this.stats.hits++;
          return cached.data;
        }
        
        this.stats.misses++;
        return null;
      } catch (error) {
        this.stats.errors++;
        return null;
      }
    }

    /**
     * Gist JSON 데이터 로드 (v8.3.0: 전역 캐싱)
     * - 세션 동안 한 번만 fetch
     * - 동시 요청 시 Promise 공유로 중복 방지
     */
    async loadGistData() {
      const now = Date.now();
      
      // 1순위: 전역 메모리 캐시 확인
      if (GistCacheReader._sessionGistData && now < GistCacheReader._sessionGistExpiry) {
        return GistCacheReader._sessionGistData;
      }
      
      // 2순위: 진행 중인 fetch가 있으면 대기
      if (GistCacheReader._fetchPromise) {
        return await GistCacheReader._fetchPromise;
      }
      
      // 3순위: 새로 fetch (Promise 공유)
      GistCacheReader._fetchPromise = this._fetchGistData();
      
      try {
        const result = await GistCacheReader._fetchPromise;
        return result;
      } finally {
        GistCacheReader._fetchPromise = null;
      }
    }
    
    /**
     * v8.3.0: 실제 Gist fetch 로직
     */
    async _fetchGistData() {
      const now = Date.now();
      
      try {
        // 캐시 버스터를 시간 단위로 (1시간마다 새로고침)
        const cacheBuster = Math.floor(now / 3600000);
        const response = await fetch(this.rawUrl + `?t=${cacheBuster}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          cache: 'default' // 브라우저 캐시 활용
        });
        
        if (!response.ok) return null;
        
        const data = await response.json();
        const expiry = now + this.GIST_CACHE_TTL;
        
        // 전역 메모리에 저장
        GistCacheReader._sessionGistData = data;
        GistCacheReader._sessionGistExpiry = expiry;
        
        // 세션 스토리지에 저장
        this._saveToSession(data, expiry);
        
        this.stats.fetches++;
        
        if (EDM_CONSTANTS.DEBUG) {
          const count = Object.keys(data?.discounts || {}).length;
          console.log(`[EDM-User] Gist fetched: ${count} items (will cache for ${this.GIST_CACHE_TTL / 60000} min)`);
        }
        
        return data;
      } catch (error) {
        if (EDM_CONSTANTS.DEBUG) {
          console.error('[EDM-User] Gist fetch error:', error);
        }
        this.stats.errors++;
        return null;
      }
    }

    /**
     * 강제 새로고침 (관리자 전용)
     */
    async refresh() {
      GistCacheReader._sessionGistData = null;
      GistCacheReader._sessionGistExpiry = 0;
      GistCacheReader._fetchPromise = null;
      
      // 세션 스토리지도 클리어
      try {
        sessionStorage.removeItem(GistCacheReader.GIST_SESSION_KEY);
        sessionStorage.removeItem(GistCacheReader.GIST_EXPIRY_KEY);
      } catch (e) {}
      
      return await this.loadGistData();
    }

    getStats() {
      return { 
        ...this.stats, 
        total: GistCacheReader._sessionGistData 
          ? Object.keys(GistCacheReader._sessionGistData.discounts || {}).length 
          : 0,
        cached: !!GistCacheReader._sessionGistData,
        expiresIn: GistCacheReader._sessionGistExpiry 
          ? Math.max(0, Math.round((GistCacheReader._sessionGistExpiry - Date.now()) / 60000))
          : 0
      };
    }
  }

  /**
   * 통합 캐시 관리자 (L1-L4) - 읽기 전용
   */
  class UserUnifiedCache {
    constructor() {
      this.memory = new Map();       // L1: 메모리
      this.expiry = new Map();       // 만료 시간 추적
      this.storageBlocked = false;
      
      this.gistCache = new GistCacheReader();  // L4
      
      this.stats = {
        hits: 0,
        misses: 0,
        l1Hits: 0,
        l2Hits: 0,
        l3Hits: 0,
        l4Hits: 0
      };
      
      // L2/L3에서 복원
      this.loadFromStorage();
      
      // v8.2.4: Gist 프리로드는 init()에서 await로 한 번만 호출
      // 생성자에서 호출 시 중복 및 경쟁 조건 발생
    }

    /**
     * L2/L3 스토리지에서 복원
     */
    loadFromStorage() {
      if (this.storageBlocked) return;
      
      let stored = null;
      let source = null;
      
      try {
        // L2: sessionStorage 우선
        if (typeof sessionStorage !== 'undefined') {
          stored = sessionStorage.getItem(EDM_CONSTANTS.STORAGE_KEY);
          if (stored) source = 'L2';
        }
        
        // L3: localStorage 폴백
        if (!stored && typeof localStorage !== 'undefined') {
          stored = localStorage.getItem(EDM_CONSTANTS.STORAGE_KEY);
          if (stored) source = 'L3';
        }
        
        if (stored) {
          let cacheData;
          try {
            cacheData = JSON.parse(stored);
          } catch (e) {
            return;
          }
          
          let loadedCount = 0;
          const now = Date.now();
          
          if (cacheData?.entries && Array.isArray(cacheData.entries)) {
            for (const [key, value, expiry] of cacheData.entries) {
              if (key == null || value == null) continue;
              if (expiry && now < expiry) {
                this.memory.set(Number(key), value);
                this.expiry.set(Number(key), expiry);
                loadedCount++;
              }
            }
          }
          
          if (EDM_CONSTANTS.DEBUG && loadedCount > 0) {
            console.log(`[EDM-User] Loaded ${loadedCount} items from ${source}`);
          }
        }
      } catch (e) {
        this.storageBlocked = true;
      }
    }

    /**
     * Gist 데이터를 L1 메모리에 프리로드
     * v8.3.0: 세션 기반 캐싱으로 중복 fetch 완전 방지
     */
    async preloadGistToMemory() {
      // v8.3.0: GistCacheReader가 세션 스토리지 기반으로 관리하므로
      // 여기서는 별도 중복 방지 불필요 (loadGistData가 Promise 공유)
      
      try {
        const gistData = await this.gistCache.loadGistData();
        if (!gistData?.discounts) return;
        
        let loadedCount = 0;
        const now = Date.now();
        
        for (const [variantId, entry] of Object.entries(gistData.discounts)) {
          if (entry.data) {
            const normalizedId = Number(variantId);
            if (!this.memory.has(normalizedId)) {
              this.memory.set(normalizedId, entry.data);
              const newExpiry = (entry.expiry && entry.expiry > now) ? entry.expiry : now + EDM_CONSTANTS.GIST_CACHE_EXPIRY_MS;
              this.expiry.set(normalizedId, newExpiry);
              loadedCount++;
            }
          }
        }
        
        if (EDM_CONSTANTS.DEBUG && loadedCount > 0) {
          console.log(`[EDM-User] Preloaded ${loadedCount} items from Gist`);
        }
      } catch (error) {
        // Silent error
      }
    }

    /**
     * 동기 캐시 조회 (L1 메모리)
     */
    get(variantId) {
      const normalizedId = Number(variantId);
      const memoryData = this.memory.get(normalizedId);
      
      if (memoryData) {
        this.stats.hits++;
        this.stats.l1Hits++;
        return memoryData;
      }
      
      this.stats.misses++;
      return null;
    }

    /**
     * 비동기 캐시 조회 (L1-L4)
     * v8.3.0: Gist 중복 fetch 방지 - 이미 프리로드된 데이터 우선 사용
     */
    async getAsync(variantId) {
      const normalizedId = Number(variantId);
      
      // L1 동기 조회 (이미 프리로드되었으면 여기서 히트)
      const syncResult = this.get(variantId);
      if (syncResult) return syncResult;
      
      // L4: Gist 캐시에서 직접 조회 (이미 캐싱된 데이터 사용, 추가 fetch 없음)
      if (this.gistCache.enabled) {
        // v8.3.0: GistCacheReader가 세션 캐시를 관리하므로 추가 fetch 없음
        const gistData = await this.gistCache.get(normalizedId);
        if (gistData) {
          this.memory.set(normalizedId, gistData);
          this.expiry.set(normalizedId, Date.now() + EDM_CONSTANTS.GIST_CACHE_EXPIRY_MS);
          this.stats.hits++;
          this.stats.misses--;
          this.stats.l4Hits++;
          return gistData;
        }
      }
      
      return null;
    }

    /**
     * 캐시 존재 여부
     */
    has(variantId) {
      return this.memory.has(Number(variantId));
    }

    /**
     * 여러 variant 일괄 조회
     */
    getMultiple(variantIds) {
      const results = new Map();
      for (const id of variantIds) {
        const data = this.get(id);
        if (data) results.set(Number(id), data);
      }
      return results;
    }

    /**
     * 히트율 계산
     */
    getHitRate() {
      const total = this.stats.hits + this.stats.misses;
      return total > 0 ? Math.round((this.stats.hits / total) * 100) : 0;
    }

    /**
     * 통계 조회
     */
    getStats() {
      return {
        total: this.memory.size,
        hits: this.stats.hits,
        misses: this.stats.misses,
        hitRate: this.getHitRate(),
        layers: {
          l1: this.stats.l1Hits,
          l2: this.stats.l2Hits,
          l3: this.stats.l3Hits,
          l4: this.stats.l4Hits
        },
        gist: this.gistCache.getStats()
      };
    }

    /**
     * 전체 캐시 덤프
     */
    dump() {
      const result = {};
      for (const [key, value] of this.memory.entries()) {
        result[key] = value;
      }
      return result;
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // 🔔 로딩 인디케이터 v7.4 (CSS 분리 - component-edm.css 연동)
  // ═══════════════════════════════════════════════════════════════════

  // v8.2.4: CSS 변수 주입 상태 추적 (injectEDMCSSVariables 내부에서 관리)
  // injectCSSVariables() 래퍼 제거 - 직접 injectEDMCSSVariables() 사용

  /**
   * 로딩 인디케이터 생성 (v7.4 - CSS 분리 버전)
   * 스타일은 component-edm.css에서, 동적 값만 data 속성으로 전달
   */
  function createLoadingIndicator(totalCount = 0) {
    const settings = EDM_SETTINGS.indicator;
    
    if (!settings.enabled || settings.showToUsers === false) return null;
    
    // v8.2.4: CSS 변수 주입은 init()에서 이미 처리됨
    
    // 모바일 감지 및 offset 설정
    const isMobile = window.innerWidth < 768;
    const offsetY = isMobile ? (settings.offsetYMobile ?? settings.offsetY) : settings.offsetY;
    
    const text = settings.text || '';
    const progressText = settings.textProgress || '';
    const subText = settings.subText || '';
    const showSpinner = settings.showSpinner !== false;
    const showProgress = settings.showProgress !== false;
    
    const initialText = progressText.replace('{current}', '0').replace('{total}', totalCount.toString());
    
    const indicator = document.createElement('div');
    indicator.className = 'edm-loading-indicator edm-state-loading';
    
    // v7.4: CSS 프리셋 선택을 data 속성으로 전달
    indicator.dataset.position = settings.position || 'top-right';
    indicator.dataset.design = settings.design || 'modern';
    indicator.dataset.animation = settings.animation || 'spring';
    indicator.dataset.animationMobile = settings.animationMobile || 'default'; // 모바일 전용 애니메이션
    
    // CSS 변수로도 전달 (모바일 애니메이션 효과 제어)
    indicator.style.setProperty('--edm-animation-mobile', settings.animationMobile || 'default');
    
    // 클래스 추가로 모바일 애니메이션 적용 (더 효율적)
    const mobileAnimClass = `edm-mobile-anim-${settings.animationMobile || 'default'}`;
    indicator.classList.add(mobileAnimClass);
    
    indicator.dataset.totalCount = totalCount;
    indicator.dataset.progressText = progressText;
    indicator.dataset.discountCount = '0';
    
    // v7.3: 향상된 접근성 속성
    indicator.setAttribute('role', 'status');
    indicator.setAttribute('aria-live', 'polite');
    indicator.setAttribute('aria-atomic', 'true');
    indicator.setAttribute('aria-label', `${text}: 0/${totalCount}`);
    
    let html = '<div class="edm-loading-header">';
    
    if (showSpinner) {
      html += '<div class="edm-spinner" aria-hidden="true"></div>';
      html += `<div class="edm-checkmark" aria-hidden="true">
        <svg class="edm-checkmark-svg" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"></polyline></svg>
      </div>`;
    }
    
    html += '<div class="edm-text-wrap">';
    html += `<div class="edm-text">${initialText}</div>`;
        if (showProgress) {
      html += `<div class="edm-progress-wrap" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"><div class="edm-progress-bar" style="width: 0%"></div></div>`;
    }
    if (subText) html += `<div class="edm-subtext">${subText}</div>`;
    html += '</div>';
    
    html += `<span class="edm-discount-count" style="display: none;">
      <span class="edm-count-icon">🏷️</span>
      <span class="edm-count-value">0</span>
    </span>`;
    
    html += '</div>';
        
    indicator.innerHTML = html;
    return indicator;
  }

  /**
   * 로딩 인디케이터 업데이트
   * v7.3: aria 속성 업데이트, 할인 발견 효과
   */
  function updateLoadingIndicator(indicator, current, total, discountCount = null) {
    if (!indicator) return;
    
    const textEl = indicator.querySelector('.edm-text');
    const progressText = indicator.dataset.progressText || '';
    
    if (textEl) {
      textEl.textContent = progressText.replace('{current}', current.toString()).replace('{total}', total.toString());
    }
    
    // 진행바 업데이트
    const progressWrap = indicator.querySelector('.edm-progress-wrap');
    const progressBar = indicator.querySelector('.edm-progress-bar');
    const percent = total > 0 ? Math.round((current / total) * 100) : 0;
    
    if (progressBar) {
      progressBar.style.width = `${percent}%`;
    }
    
    // aria 속성 업데이트
    if (progressWrap) {
      progressWrap.setAttribute('aria-valuenow', percent.toString());
    }
    
    // 할인 카운터 업데이트
    if (discountCount !== null && discountCount > 0) {
      const prevCount = parseInt(indicator.dataset.discountCount || '0', 10);
      indicator.dataset.discountCount = discountCount.toString();
      
      const counterEl = indicator.querySelector('.edm-discount-count');
      const counterValueEl = indicator.querySelector('.edm-count-value');
      
      if (counterEl && counterValueEl) {
        counterEl.style.display = 'inline-flex';
        counterEl.setAttribute('aria-hidden', 'false');
        counterValueEl.textContent = discountCount.toString();
        
        // 첫 번째 할인 발견 시 효과
        if (prevCount === 0 && discountCount > 0) {
          indicator.classList.add('edm-has-discounts');
        }
      }
    }
    
    // 접근성 - 상태 업데이트
    const currentDiscount = indicator.dataset.discountCount || '0';
    indicator.setAttribute('aria-label', 
      `Discount calculation in progress: ${current}/${total}. ${currentDiscount} discounts found.`);
  }

  /**
   * 로딩 인디케이터 숨기기
   * v7.3: aria-hidden 기반 퇴장 애니메이션, 완료 상태 처리
   */
  function hideLoadingIndicator(indicator, delay = 2000) {
    if (!indicator?.parentNode) return;
    
    const settings = EDM_SETTINGS.indicator;
    const completeText = settings.textComplete || '';
    const discountCount = parseInt(indicator.dataset.discountCount || '0', 10);
    
    // 완료 상태로 전환
    indicator.classList.remove('edm-state-loading');
    indicator.classList.add('edm-state-complete');
    
    const textEl = indicator.querySelector('.edm-text');
    if (textEl) {
      textEl.textContent = discountCount > 0 ? `${completeText} (${discountCount})` : completeText;
    }
    
    // 진행바 100%
    const progressWrap = indicator.querySelector('.edm-progress-wrap');
    const progressBar = indicator.querySelector('.edm-progress-bar');
    if (progressBar) {
      progressBar.style.width = '100%';
    }
    if (progressWrap) {
      progressWrap.setAttribute('aria-valuenow', '100');
    }
    
    // 접근성 - 완료 알림
    indicator.setAttribute('aria-label', 
      `Discount calculation complete. ${discountCount} discounts found.`);
    
    setTimeout(() => {
      indicator.setAttribute('aria-hidden', 'true');
      setTimeout(() => {
        if (indicator.parentNode) indicator.parentNode.removeChild(indicator);
      }, 300);
    }, delay);
  }

  // ═══════════════════════════════════════════════════════════════════
  // 🎨 할인 UI 적용
  // ═══════════════════════════════════════════════════════════════════

  /**
   * 가격 컨테이너에 할인 적용 (edm-main.js와 동일한 로직)
   * v8.2.1: 캐싱 기반 최적화 - 중복 계산 제거
   */
  function applyDiscountToPriceContainer(priceContainer, discount) {
    if (!discount?.hasDiscount || discount.discountPercent <= 0) return;
    if (priceContainer.dataset.edmProcessed === 'true') return;

    // Wokiee 테마 구조: [data-edm-price-container] > .price__container > .price
    let priceDiv = priceContainer.querySelector('.price__container .price');
    const priceContainerInner = priceContainer.querySelector('.price__container');
    
    if (!priceDiv) {
      priceDiv = priceContainer.querySelector('.price');
    }
    if (!priceDiv && priceContainer.classList.contains('price')) {
      priceDiv = priceContainer;
    }
    
    // 사이즈 클래스 결정 (edm-main.js 방식)
    let priceSizeClass = 'h5';
    if (priceContainerInner?.dataset?.size) {
      priceSizeClass = priceContainerInner.dataset.size;
    } else if (priceContainer.dataset?.size) {
      priceSizeClass = priceContainer.dataset.size;
    } else if (priceDiv) {
      // 기존 클래스에서 h1-h6 추출
      const mainPriceForSize = priceDiv.querySelector('.price__main');
      const salePriceForSize = priceDiv.querySelector('.price__sale');
      const sizeMatch = (mainPriceForSize?.className || salePriceForSize?.className || '').match(/\b(h[1-6])\b/);
      if (sizeMatch) priceSizeClass = sizeMatch[1];
    }
    
    const finalPrice = discount.discountedPrice || discount.finalPrice;
    
    // Case: priceDiv가 없으면 직접 생성
    if (!priceDiv) {
      priceDiv = document.createElement('div');
      priceDiv.className = 'price';
      
      // v8.4.3: 헬퍼 함수 사용
      const { regularDiv, saleDiv } = createPriceElements(priceSizeClass, discount.originalPrice, finalPrice);
      priceDiv.appendChild(regularDiv);
      priceDiv.appendChild(saleDiv);
      
      // priceContainerInner가 있으면 그 안에, 없으면 priceContainer에 직접
      if (priceContainerInner) {
        priceContainerInner.appendChild(priceDiv);
      } else {
        priceContainer.appendChild(priceDiv);
      }
      
      priceContainer.classList.add('price--on-sale');
      priceContainer.dataset.edmProcessed = 'true';
      return;
    }
    
    const mainPriceDiv = priceDiv.querySelector('.price__main, [data-edm-original-price]');
    let regularDiv = priceDiv.querySelector('.price__regular, [data-edm-compare-wrapper]');
    let saleDiv = priceDiv.querySelector('.price__sale, [data-edm-sale-price]');
    
    // Case 1: price__main만 있는 경우 (할인 없는 원가 표시)
    if (mainPriceDiv && !regularDiv && !saleDiv) {
      mainPriceDiv.style.display = 'none';
      
      // v8.4.3: 헬퍼 함수 사용
      const created = createPriceElements(priceSizeClass, discount.originalPrice, finalPrice);
      regularDiv = created.regularDiv;
      saleDiv = created.saleDiv;
      
      priceDiv.appendChild(regularDiv);
      priceDiv.appendChild(saleDiv);
    }
    // Case 2: 이미 price__regular + price__sale 구조인 경우
    else if (regularDiv || saleDiv) {
      if (regularDiv) {
        const compareEl = regularDiv.querySelector('s, [data-edm-compare-price]');
        if (compareEl) {
          compareEl.textContent = PriceFormatter.money(discount.originalPrice);
        } else {
          regularDiv.innerHTML = `<s data-edm-compare-price>${PriceFormatter.money(discount.originalPrice)}</s>`;
        }
        regularDiv.classList.add('edm-price-compare');
        regularDiv.style.display = '';
      } else {
        // v8.4.3: 헬퍼 함수로 생성 후 regularDiv만 사용
        const created = createPriceElements(priceSizeClass, discount.originalPrice, finalPrice);
        regularDiv = created.regularDiv;
        priceDiv.insertBefore(regularDiv, saleDiv);
      }
      
      if (saleDiv) {
        saleDiv.textContent = PriceFormatter.money(finalPrice);
        saleDiv.classList.add('edm-price-sale');
        saleDiv.style.display = '';
      } else {
        // v8.4.3: 헬퍼 함수로 생성 후 saleDiv만 사용
        const created = createPriceElements(priceSizeClass, discount.originalPrice, finalPrice);
        saleDiv = created.saleDiv;
        priceDiv.appendChild(saleDiv);
      }
      
      if (mainPriceDiv) mainPriceDiv.style.display = 'none';
    }
    // Case 3: 아무것도 없는 경우 - 직접 생성
    else {
      // v8.4.3: 헬퍼 함수 사용
      const created = createPriceElements(priceSizeClass, discount.originalPrice, finalPrice);
      regularDiv = created.regularDiv;
      saleDiv = created.saleDiv;
      
      priceDiv.appendChild(regularDiv);
      priceDiv.appendChild(saleDiv);
    }
    
    // 할인가 영역에 마커 추가
    if (saleDiv) {
      saleDiv.dataset.edmPrice = 'true';
    }
    
    priceContainer.classList.add('price--on-sale');
    priceContainer.dataset.edmProcessed = 'true';
  }

  /**
   * 배지 추가 (v8.3.5 - badges__container 완전 통합)
   * 
   * Liquid badges 시스템의 [data-edm-sale-slot] 요소를 EDM 배지로 변환
   * - 기본 할인(compare_at_price)이 있으면: 테마 sale 배지 유지
   * - EDM 할인만 있으면: edm-sale-slot을 EDM 배지로 표시
   * 
   * 배지 위치 (badges__container 내):
   * 1. sold_out - 품절 배지
   * 2. sale / edm-badge - 할인 배지 (같은 위치)
   * 3. countdown - 카운트다운 타이머
   * 4. featured - 피처드 배지
   * 5. new - 신상품 배지
   */
  function addBadge(card, discount) {
    if (!discount?.hasDiscount || discount.discountPercent <= 0) return;
    if (card.dataset.edmBadgeAdded === 'true') return;

    const { badge, applyToSoldOut, config } = EDM_SETTINGS;

    // 배지/라벨 비활성화 확인 (통합된 badge 구조 사용)
    if (badge.show === false || badge.labelEnabled === false) {
      card.dataset.edmBadgeAdded = 'true';
      return;
    }
    
    // v8.3.5: 섹션별 EDM 비활성화 체크 (data-edm-disabled 속성)
    const badgesContainer = card.querySelector('.badges__container');
    if (badgesContainer?.dataset.edmDisabled === 'true') {
      if (config.debug) console.log(`[EDM-User] EDM badge disabled for this section`);
      card.dataset.edmBadgeAdded = 'true';
      return;
    }
    
    // Sold Out 상품 체크 (applyToSoldOut이 false면 스킵)
    const hasSoldOutBadge = card.querySelector('.badges-item__sold-out');
    if (!applyToSoldOut && hasSoldOutBadge) {
      if (config.debug) console.log(`[EDM-User] Sold out, applyToSoldOut=false, skipping badge`);
      card.dataset.edmBadgeAdded = 'true';
      return;
    }
    
    // [data-edm-sale-slot] 찾기 - badges__container 내에서
    let saleSlot = card.querySelector('.badges__container [data-edm-sale-slot]');
    
    // 폴백: card 전체에서 찾기
    if (!saleSlot) {
      saleSlot = card.querySelector('[data-edm-sale-slot]');
    }
    
    // v8.4.3: 배지 크기 클래스 감지 (badges-item__small vs badges-item__medium)
    // 기존 배지 또는 컨테이너 내 다른 배지에서 크기 클래스 추론
    let badgeSizeClass = 'badges-item__small'; // 기본값
    if (saleSlot) {
      if (saleSlot.classList.contains('badges-item__medium')) {
        badgeSizeClass = 'badges-item__medium';
      }
    } else {
      // 컨테이너 내 다른 배지에서 크기 클래스 추론
      const existingBadge = card.querySelector('.badges-item__medium, .badges-item__small');
      if (existingBadge?.classList.contains('badges-item__medium')) {
        badgeSizeClass = 'badges-item__medium';
      }
    }
    
    if (!saleSlot) {
      // sale slot이 없으면 badges__container에 직접 추가
      const badgesContainer = card.querySelector('.badges__container');
      if (!badgesContainer) {
        if (config.debug) console.log(`[EDM-User] No badges__container found`);
        return;
      }
      
      // 새 EDM 배지 생성하여 badges__container에 추가 (감지된 크기 클래스 사용)
      saleSlot = document.createElement('span');
      saleSlot.className = `${badgeSizeClass} badges-item__sale edm-sale-slot`;
      saleSlot.dataset.edmSaleSlot = discount.variantId?.toString() || '';
      saleSlot.style.display = 'none';
      
      // sold_out 배지 다음 위치에 삽입
      const soldOutBadge = badgesContainer.querySelector('.badges-item__sold-out');
      if (soldOutBadge) {
        soldOutBadge.insertAdjacentElement('afterend', saleSlot);
      } else {
        badgesContainer.insertBefore(saleSlot, badgesContainer.firstChild);
      }
      
      if (config.debug) console.log(`[EDM-User] Created new sale slot in badges__container (size: ${badgeSizeClass})`);
    }
    
    // v8.3.6: Label Priority 로직 개선 - 기본 할인 배지 존재 여부 확인
    const isPlaceholder = saleSlot.classList.contains('edm-sale-slot');
    const priority = badge.labelPriority || 'regular_first';
    
    if (!isPlaceholder) {
      // 기존 테마 할인 배지가 있는 경우 (compare_at_price 할인)
      // Priority에 따라 처리
      if (priority === 'regular_first') {
        // regular_first: 기본 할인 배지 유지, EDM 배지 추가 안함
        if (config.debug) console.log(`[EDM-User] Regular discount badge exists, keeping theme badge (priority: regular_first)`);
        card.dataset.edmBadgeAdded = 'true';
        return;
      } else if (priority === 'edm_first' || priority === 'edm_only') {
        // edm_first/edm_only: 기존 배지 숨기고 EDM 배지 표시
        if (config.debug) console.log(`[EDM-User] Hiding regular badge for EDM (priority: ${priority})`);
        saleSlot.classList.add('edm-hidden');
        saleSlot.style.display = 'none';
        
        // 새 EDM 슬롯 생성 (기존 배지의 크기 클래스 유지)
        const newSlot = document.createElement('span');
        newSlot.className = `${badgeSizeClass} badges-item__sale edm-sale-slot`;
        newSlot.dataset.edmSaleSlot = discount.variantId?.toString() || '';
        saleSlot.insertAdjacentElement('afterend', newSlot);
        saleSlot = newSlot;
      } else if (priority === 'both') {
        // both: 기존 배지 유지하고 EDM 배지도 추가
        if (config.debug) console.log(`[EDM-User] Showing both badges (priority: both)`);
        const newSlot = document.createElement('span');
        newSlot.className = `${badgeSizeClass} badges-item__sale edm-sale-slot`;
        newSlot.dataset.edmSaleSlot = discount.variantId?.toString() || '';
        saleSlot.insertAdjacentElement('afterend', newSlot);
        saleSlot = newSlot;
      }
    }
    
    // 이미 EDM 배지로 변환됨
    if (saleSlot.classList.contains('edm-badge')) {
      card.dataset.edmBadgeAdded = 'true';
      return;
    }
    
    // 라벨 텍스트 생성 (통합된 badge 구조 사용)
    const template = EDM_SETTINGS.badge.labelTemplate || '[percent]% off';
    const labelText = parseTemplate(template, {
      percent: discount.discountPercent,
      amount: PriceFormatter.money(discount.discountAmount || 0),
      originalPrice: PriceFormatter.money(discount.originalPrice || 0),
      discountedPrice: PriceFormatter.money(discount.discountedPrice || 0)
    });
    
    // v8.4.2: CSS 클래스 기반 스타일링 (인라인 스타일 최소화)
    // 색상은 CSS 변수로 정의되어 있으므로 클래스만 추가
    const pct = discount.discountPercent;
    let badgeClass = '';
    
    if (pct >= 30) {
      badgeClass = ' badge-hot';
    } else if (pct >= 20) {
      badgeClass = ' badge-great';
    }
    
    // edm-sale-slot을 EDM 배지로 변환
    // CSS 클래스로 스타일 적용 (--color-save-badge-*, --color-save-badge-text 변수 사용)
    // v8.4.3: 감지된 크기 클래스 사용
    saleSlot.className = `${badgeSizeClass} badges-item__sale edm-badge${badgeClass}`;
    saleSlot.style.cssText = 'display: inline-block;'; // display만 인라인으로
    saleSlot.innerHTML = `<span class="badge-percent">${labelText}</span>`;
    saleSlot.dataset.variantId = discount.variantId?.toString();
    saleSlot.dataset.discountPercent = pct.toString();
    // edm-sale-slot 클래스 제거 (변환 완료)
    saleSlot.classList.remove('edm-sale-slot');
    
    card.dataset.edmBadgeAdded = 'true';
    
    if (config.debug) {
      console.log(`[EDM-User] EDM badge applied in badges__container`, {
        variantId: discount.variantId,
        percent: pct,
        badgeClass: badgeClass || 'default',
        sizeClass: badgeSizeClass,
        inBadgesContainer: !!saleSlot.closest('.badges__container')
      });
    }
  }

  /**
   * 할인 정보 태그 추가 (edm-main.js에서 복사)
   */
  function addDiscountInfo(card, data) {
    // Related 섹션에서 discount-tag 숨김
    if (card.dataset.hideEdmDiscountInfo === 'true') {
      if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] Discount info hidden for related product variant ${data.variantId}`);
      return;
    }
    
    // v8.3.5: 섹션별 EDM 할인 정보 비활성화 체크
    const badgesContainer = card.querySelector('.badges__container');
    if (badgesContainer?.dataset.edmInfoDisabled === 'true') {
      if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] Discount info disabled for this section`);
      return;
    }
    
    // 할인이 없으면 할인 정보 태그 추가 안함
    if (!data || !data.hasDiscount || data.discountPercent <= 0) {
      if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] No valid discount for info tag, variant ${data?.variantId}`);
      return;
    }
    
    if (!data.discounts?.length) return;
    
    const selectors = EDM_SELECTORS.IMAGE_CONTAINER.split(', ');
    const imageContainer = findImageContainer(card, selectors);
    if (!imageContainer) return;
    
    // 기존 정보 제거
    const existing = imageContainer.querySelector('.edm-product-discount-info');
    if (existing) existing.remove();
    
    const infoDiv = document.createElement('div');
    infoDiv.className = 'edm-product-discount-info';
    infoDiv.innerHTML = data.discounts
      .map(d => `<span class="edm-discount-tag">${d.title || d.type}</span>`)
      .join('');
    
    imageContainer.appendChild(infoDiv);
    
    if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] Added discount info for variant ${data.variantId}: ${data.discounts.length} discounts`);
  }

  /**
   * 상품 카드에 할인 적용
   */
  function applyDiscountToCard(card, discount) {
    if (!discount?.hasDiscount || discount.discountPercent <= 0) {
      card.dataset.edmNoDiscount = 'true';
      return false;
    }

    if (card.dataset.edmProcessed === 'true') return false;

    // v6.7.17: Sold Out 상품 체크 - applyToSoldOut 옵션에 따라 EDM 할인 적용 여부 결정
    const soldOutBadge = card.querySelector('.badges-item__sold-out');
    if (soldOutBadge && !EDM_SETTINGS.applyToSoldOut) {
      if (EDM_SETTINGS.config.debug) {
        console.log(`[EDM-User] Sold out product detected for variant ${discount.variantId}, skipping EDM discount (applyToSoldOut: false)`);
      }
      card.dataset.edmSkipped = 'sold_out';
      return false;
    }

    // v6.8.0: 기본 할인 상품 체크 (compare_at_price) - EDM 할인 비교 로직 추가
    // percent 타입에서 .hide 클래스가 있는 배지는 compare_at_price가 없는 것이므로 제외
    const priceContainer = card.querySelector('[data-edm-price-container]') ||
                           card.querySelector('.price__container') ||
                           card.querySelector('.price');

    const hasCompareDiscountAttr = priceContainer?.dataset?.hasCompareDiscount === 'true' ||
                                   priceContainer?.classList?.contains('has-compare-discount');

    // 실제로 보이는 기본 할인 배지 체크 (.hide 클래스 제외)
    const visibleRegularSaleBadge = card.querySelector(
      '.badges-item__sale:not(.edm-badge):not(.hide),' +
      '.badges-item__sale__counter:not(.edm-badge):not(.hide)'
    );
    // v8.2.1: 캐싱된 기본 할인 정보 사용 (중복 계산 제거)
    const regularInfo = getRegularDiscountInfo(card, discount);
    const compareDiscountPercent = regularInfo.percent || 0;
    
    // v8.4.1: 실제 할인율이 0보다 커야 "기본 할인 있음"으로 판단
    // hasCompareDiscountAttr/visibleRegularSaleBadge만으로는 부족, 실제 할인율 확인 필요
    const hasActualCompareDiscount = compareDiscountPercent > 0 && (hasCompareDiscountAttr || visibleRegularSaleBadge);

    if (EDM_SETTINGS.config.debug) {
      console.log(`[EDM-User] Compare discount: ${compareDiscountPercent}% (source: ${regularInfo.source}), EDM: ${discount.discountPercent}%, hasActual: ${hasActualCompareDiscount}`);
    }

    // v6.8.0: EDM 할인이 기본 할인보다 더 좋은지 확인
    const edmIsBetter = discount.discountPercent > compareDiscountPercent;
    const priority = EDM_SETTINGS.badge?.labelPriority || 'regular_first';

    // 기본 할인 처리 로직 (priority 설정에 따라) - 실제 할인율 > 0 일 때만
    if (hasActualCompareDiscount) {
      if (priority === 'regular_first') {
        // regular_first: 기본 할인이 있으면 항상 기본 배지 유지 (EDM 할인율 관계없이)
        if (EDM_SETTINGS.config.debug) {
          console.log(`[EDM-User] Regular discount exists (${compareDiscountPercent}%), keeping theme badge (priority: regular_first)`);
        }
        card.dataset.edmSkipped = 'compare_discount';
        card.dataset.edmComparePercent = compareDiscountPercent.toString();
        // 기본 할인 배지는 유지하고, 가격 업데이트 없이 종료
        return false;
      } else if (priority === 'edm_first' || priority === 'edm_only') {
        // edm_first/edm_only: 기본 할인 배지 숨기고 EDM 표시
        if (EDM_SETTINGS.config.debug) {
          console.log(`[EDM-User] Hiding regular badge for EDM (priority: ${priority}), Compare: ${compareDiscountPercent}%, EDM: ${discount.discountPercent}%`);
        }
        card.dataset.edmReplacedCompare = 'true';
        card.dataset.edmComparePercent = compareDiscountPercent.toString();

        // 기존 기본 할인 배지 숨기기
        const regularBadges = card.querySelectorAll(
          '.badges-item__sale:not(.edm-badge),' +
          '.badges-item__sale__counter:not(.edm-badge)'
        );
        regularBadges.forEach(badge => {
          badge.classList.add('edm-hidden');
          badge.style.display = 'none';
        });
      } else if (priority === 'both') {
        // both: 기본 할인 + EDM 배지 모두 표시 (가격은 EDM으로 업데이트)
        if (EDM_SETTINGS.config.debug) {
          console.log(`[EDM-User] Showing both badges (priority: both), Regular: ${compareDiscountPercent}%, EDM: ${discount.discountPercent}%`);
        }
        card.dataset.edmComparePercent = compareDiscountPercent.toString();
        // badges__container에 priority 표시
        const badgesContainer = card.querySelector('.badges__container');
        if (badgesContainer) {
          badgesContainer.dataset.priority = 'both';
        }
      }
    }
    
    // 가격 컨테이너에 할인 적용
    if (priceContainer) {
      applyDiscountToPriceContainer(priceContainer, discount);
    }
    
    // 배지 추가 (통합된 badge 설정 사용)
    const showBadges = EDM_SETTINGS.badge?.show !== false;
    if (showBadges) {
      addBadge(card, discount);
    }
    
    // 할인 정보 태그 추가 (통합된 badge 설정 사용)
    const showDiscountInfo = EDM_SETTINGS.badge?.showDiscountInfo !== false;
    if (showDiscountInfo && discount.discounts?.length) {
      addDiscountInfo(card, discount);
    }
    
    card.dataset.edmProcessed = 'true';
    card.dataset.edmVariantId = discount.variantId?.toString();
    return true;
  }

  // ═══════════════════════════════════════════════════════════════════
  // 🚀 메인 초기화
  // ═══════════════════════════════════════════════════════════════════

  // 전역 캐시 인스턴스
  let cache = null;
  let loadingIndicator = null;
  let initialized = false;

  /**
   * 모든 상품 카드 처리 (edm-main.js와 동일한 진행 상태 업데이트)
   */
  async function processAllCards() {
    const cards = document.querySelectorAll(EDM_SELECTORS.CARDS);
    if (cards.length === 0) return { total: 0, applied: 0, discountedVariants: 0 };
    
    // variant별로 그룹화
    const variantGroups = new Map();
    const noVariantCards = [];
    
    for (const card of cards) {
      if (card.dataset.edmProcessed === 'true') continue;
      
      const variantId = extractVariantId(card);
      if (variantId) {
        if (!variantGroups.has(variantId)) {
          variantGroups.set(variantId, []);
        }
        variantGroups.get(variantId).push(card);
      } else {
        noVariantCards.push(card);
        card.dataset.edmSkipped = 'true';
      }
    }
    
    const totalVariants = variantGroups.size;
    let appliedCardsCount = 0;  // 실제 카드에 적용된 수
    let discountedVariants = 0; // 할인이 있는 variant 수 (인디케이터용)
    const BATCH_SIZE = 5;
    
    const variantEntries = Array.from(variantGroups.entries());
    
    for (let i = 0; i < variantEntries.length; i += BATCH_SIZE) {
      const batch = variantEntries.slice(i, i + BATCH_SIZE);
      
      for (const [variantId, cardList] of batch) {
        const discount = cache.get(variantId);
        
        // v7.3.1: variant별로 할인 존재 여부 확인 (카드 수가 아닌 variant 수 카운트)
        let variantHasDiscount = false;
        
        for (const card of cardList) {
          if (discount && applyDiscountToCard(card, discount)) {
            appliedCardsCount++;
            variantHasDiscount = true;
          }
        }
        
        // 이 variant에 할인이 적용되었으면 카운트 증가
        if (variantHasDiscount) {
          discountedVariants++;
        }
      }
      
      // 진행 상황 업데이트 (로딩 인디케이터) - discountedVariants 사용
      if (loadingIndicator) {
        updateLoadingIndicator(
          loadingIndicator,
          Math.min(i + BATCH_SIZE, totalVariants),
          totalVariants,
          discountedVariants  // variant 기준 할인 수
        );
      }
      
      // 배치 간 짧은 대기
      if (i + BATCH_SIZE < variantEntries.length) {
        await new Promise(r => setTimeout(r, 10));
      }
    }
    
    return { total: cards.length, applied: appliedCardsCount, variants: totalVariants, discountedVariants };
  }

  /**
   * 검색 결과에 할인 적용
   */
  function applyToSearchResults(container = document) {
    const searchContainers = container.querySelectorAll(EDM_SELECTORS.predictiveSearch.container);
    let appliedCount = 0;
    
    for (const searchContainer of searchContainers) {
      const items = searchContainer.querySelectorAll(EDM_SELECTORS.predictiveSearch.item);
      
      for (const item of items) {
        if (isElementAlreadyApplied(item)) continue;
        
        const variantId = extractVariantId(item);
        if (!variantId) continue;
        
        const discount = cache.get(variantId);
        if (!discount?.hasDiscount) continue;
        
        const priceEl = item.querySelector(EDM_SELECTORS.predictiveSearch.price);
        if (priceEl && !isElementAlreadyApplied(priceEl)) {
          const mainPrice = priceEl.querySelector('.price__main');
          if (mainPrice) {
            mainPrice.innerHTML = createDiscountPriceHTML(discount, 'search');
          }
          markElementsAsApplied(priceEl, item);
          appliedCount++;
        }
      }
    }
    
    return appliedCount;
  }

  /**
   * 사이드바에 할인 적용
   */
  function applyToSidebar(container = document) {
    const sidebarItems = container.querySelectorAll(EDM_SELECTORS.sidebar.item);
    let appliedCount = 0;
    
    for (const item of sidebarItems) {
      if (isElementAlreadyApplied(item)) continue;

      let variantId = extractVariantId(item);
      if (!variantId) {
        variantId = findVariantIdByHandle(item, cache);
      }
      if (!variantId) continue;

      const applied = getDiscountAndApply(variantId, (discount) => {
        const priceEl = item.querySelector(EDM_SELECTORS.sidebar.price);
        if (priceEl && !isElementAlreadyApplied(priceEl)) {
          priceEl.innerHTML = createDiscountPriceHTML(discount, 'sidebar');
          markElementsAsApplied(priceEl, item);
          return true;
        }
        return false;
      });

      if (applied) appliedCount++;
    }
    
    return appliedCount;
  }

  /**
   * 컨테이너에 할인 적용
   */
  function applyDiscountsToContainer(container = document) {
    const cards = container.querySelectorAll(EDM_SELECTORS.CARDS);
    let appliedCount = 0;
    
    for (const card of cards) {
      if (card.dataset.edmProcessed === 'true') continue;
      
      const variantId = extractVariantId(card);
      if (!variantId) continue;
      
      const discount = cache.get(variantId);
      if (discount && applyDiscountToCard(card, discount)) {
        appliedCount++;
      }
    }
    
    return appliedCount;
  }

  /**
   * 상품 상세 페이지에 할인 적용 (복원)
   */
  async function applyToMainProduct() {
    // 상품 페이지 컨테이너 찾기 (#ProductInfo-{{ section.id }})
    const productInfoContainer = document.querySelector('[id^="ProductInfo-"]');
    if (!productInfoContainer) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Main product: No product info container found');
      return 0;
    }

    // 가격 컨테이너 찾기
    const priceContainer = productInfoContainer.querySelector('[data-edm-price-container]');
    if (!priceContainer) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Main product: No EDM price container found');
      return 0;
    }

    // 변종 ID 추출
    const variantId = priceContainer.dataset.variantId || 
                     productInfoContainer.querySelector('[data-variant-id]')?.dataset.variantId;
    if (!variantId) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Main product: No variant ID found');
      return 0;
    }

    // 캐시에서 할인 정보 가져오기
    const discount = cache.get(variantId);
    if (discount?.hasDiscount) {
      // 가격 적용
      applyDiscountToPriceContainer(priceContainer, discount);
      
      // 배지 적용 (기본 할인배지와 호환)
      applyBadgeToMainProduct(productInfoContainer, discount);
      
      if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] Applied to main product: variant ${variantId}`);
      return 1;
    }

    if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] Main product: No discount for variant ${variantId}`);
    return 0;
  }

  /**
   * 상품 상세 페이지에 할인 배지 적용 (v8.3 - 가격 옆 가로 배치)
   * 배지를 가격 컨테이너 오른쪽에 인라인으로 배치
   */
  function applyBadgeToMainProduct(container, discount) {
    if (!discount?.discountPercent > 0) return false;

    // 스키마 설정 확인: 배지 표시 여부 (통합된 badge 구조 사용)
    const badgeSettings = EDM_SETTINGS.badge;
    if (badgeSettings.show === false) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Main product badge: Badges disabled in theme settings');
      return false;
    }

    if (badgeSettings.labelEnabled === false) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Main product badge: Labels disabled in theme settings');
      return false;
    }

    // 라벨 우선순위 설정 확인
    const labelPriority = badgeSettings.labelPriority;

    // 기본 할인배지 확인 (기존 compare_at_price 배지)
    const existingSaleBadge = container.querySelector('.badges-item__sale:not(.edm-badge)');
    if (existingSaleBadge) {
      const style = window.getComputedStyle(existingSaleBadge);
      if (style.display !== 'none') {
        // 우선순위에 따른 처리
        if (labelPriority === 'regular_first') {
          // Regular First: 기본 할인 배지가 있으면 EDM 배지 표시 안함
          if (EDM_CONSTANTS.DEBUG) {
            console.log('[EDM-User] Main product badge: Regular sale badge exists, skipping EDM badge (regular_first priority)');
          }
          return false;
        } else if (labelPriority === 'edm_first' || labelPriority === 'edm_only') {
          // EDM First/Only: 기존 할인 배지 숨기고 EDM 배지만 표시
          existingSaleBadge.style.display = 'none';
          existingSaleBadge.classList.add('edm-hidden-by-priority');
          if (EDM_CONSTANTS.DEBUG) {
            console.log(`[EDM-User] Main product badge: Hiding regular badge for EDM priority (${labelPriority})`);
          }
        }
      }
    }

    // v8.3: 가격 컨테이너 찾기 (가로 배치용)
    const priceContainerEl = container.querySelector('.product__price-container');
    if (!priceContainerEl) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Main product badge: No price container found');
      return false;
    }

    // EDM 인라인 배지가 이미 있는지 확인
    if (priceContainerEl.querySelector('.edm-inline-badge')) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Main product badge: EDM inline badge already exists');
      return false;
    }

    // 라벨 템플릿 적용 (통합된 badge 구조 사용)
    const template = badgeSettings.labelTemplate || '[percent]% off';
    const labelText = parseTemplate(template, {
      percent: discount.discountPercent,
      amount: PriceFormatter.money(discount.discountAmount || 0),
      originalPrice: PriceFormatter.money(discount.originalPrice || 0),
      discountedPrice: PriceFormatter.money(discount.discountedPrice || 0)
    });

    // 색상 결정 - 통합된 badge 구조 사용
    let badgeColor = badgeSettings.color || '#A0F68A';
    let badgeClass = '';
    
    if (discount.discountPercent >= 40) {
      badgeColor = badgeSettings.colorHot || '#ff3e3e';
      badgeClass = ' badge-hot';
    } else if (discount.discountPercent >= 20) {
      badgeColor = badgeSettings.colorGreat || '#ff6b35';
      badgeClass = ' badge-great';
    }
    const textColor = getContrastColor(badgeColor.startsWith('var(') ? '#A0F68A' : badgeColor);

    // v8.3: 가격 요소 찾기 (price.liquid에서 렌더링된 요소)
    const priceElement = priceContainerEl.querySelector('[id^="price-"]');
    const priceContentEl = priceElement?.querySelector('.price__container') || priceElement;
    
    if (!priceContentEl) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Main product badge: No price element found');
      return false;
    }

    // v8.3: 가격 + 배지 래퍼 생성 (가로 배치)
    let wrapper = priceContainerEl.querySelector('.edm-price-badge-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'edm-price-badge-wrapper';
      
      // 기존 가격 컨텐츠를 래퍼로 이동
      if (priceElement && priceElement.parentNode === priceContainerEl) {
        priceContainerEl.insertBefore(wrapper, priceElement);
        wrapper.appendChild(priceElement);
      } else if (priceContentEl) {
        priceContentEl.parentNode.insertBefore(wrapper, priceContentEl);
        wrapper.appendChild(priceContentEl);
      }
    }

    // EDM 인라인 배지 생성
    const badgeEl = document.createElement('div');
    badgeEl.className = `badges-item__small edm-badge edm-inline-badge${badgeClass}`;
    badgeEl.style.cssText = `background: ${badgeColor}; color: ${textColor};`;
    badgeEl.innerHTML = `<span class="badge-percent">${labelText}</span>`;
    badgeEl.dataset.edmBadge = 'true';
    badgeEl.dataset.variantId = discount.variantId?.toString();

    // 래퍼에 배지 추가 (가격 오른쪽)
    wrapper.appendChild(badgeEl);

    if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] Main product badge: Applied ${discount.discountPercent}% inline discount badge`);
    return true;
  }

  /**
   * 비교 페이지 처리 (Wokiee 테마에 맞게 수정)
   * Wokiee 테마는 .compare-template__table 대신 .wokiee-compare__item 사용
   */
  async function processComparePage() {
    // Wokiee 테마의 비교 페이지 컨테이너 찾기
    const compareContainer = document.querySelector('.compare-ajax-content');
    if (!compareContainer) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Compare: No .compare-ajax-content found');
      return 0;
    }
    
    // .wokiee-compare__item 요소들 찾기 (AJAX 로드된 상품들)
    const compareItems = compareContainer.querySelectorAll('.wokiee-compare__item');
    if (!compareItems.length) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Compare: No .wokiee-compare__item found');
      return 0;
    }
    
    if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Processing compare page, items:', compareItems.length);
    
    let appliedCount = 0;
    
    for (const item of compareItems) {
      if (item.dataset.edmProcessed === 'true') continue;
      
      // variant ID 추출: data-item="variant-{id}" 형식
      const dataItem = item.dataset.item || '';
      const variantId = dataItem.replace('variant-', '');
      
      if (!variantId) {
        if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Compare: No variant ID in item');
        continue;
      }
      
      // 가격 컨테이너 찾기
      const priceContainer = item.querySelector('[data-edm-price-container]') ||
                            item.querySelector('.price__container');
      if (!priceContainer) {
        if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] Compare: No price container for variant ${variantId}`);
        continue;
      }
      
      // 캐시에서 할인 정보 가져오기
      const discount = cache.get(variantId);
      if (discount?.hasDiscount) {
        applyDiscountToPriceContainer(priceContainer, discount);
        appliedCount++;
        if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] Applied to compare: variant ${variantId}`);
        
        // 배지도 처리
        const badgeContainer = item.querySelector('.badges-item__medium')?.parentElement ||
                              item.querySelector('.product-card__badge-list');
        if (badgeContainer) {
          applyBadge(badgeContainer, discount);
        }
      }
      
      item.dataset.edmProcessed = 'true';
    }
    
    if (EDM_CONSTANTS.DEBUG) console.log(`[EDM-User] Compare page: applied ${appliedCount} discounts`);
    return appliedCount;
  }

  /**
   * 모든 영역에 할인 적용
   */
  async function applyToAll() {
    const results = {
      cards: await processAllCards(),
      search: applyToSearchResults(),
      sidebar: applyToSidebar(),
      comparePage: 0,
      mainProduct: await applyToMainProduct() // 상품 페이지 기능 복원
    };
    
    // 비교 페이지 처리 (Wokiee 테마: .compare-ajax-content 사용)
    // 참고: 비교 페이지는 AJAX 로드 후 MutationObserver로 처리됨
    // 초기 로드 시 이미 컨텐츠가 있으면 처리
    const compareContainer = document.querySelector('.compare-ajax-content');
    if (compareContainer && compareContainer.children.length > 0) {
      results.comparePage = await processComparePage();
    }
    
    const total = results.cards.applied + results.search + results.sidebar + results.comparePage + results.mainProduct;
    
    if (EDM_CONSTANTS.DEBUG && total > 0) {
      console.log('[EDM-User] Applied discounts:', results);
    }
    
    return { total, ...results };
  }

  /**
   * MutationObserver 설정
   */
  function setupObserver() {
    let debounceTimer = null;
    
    const observer = new MutationObserver((mutations) => {
      const hasRelevantChanges = mutations.some(m => 
        m.addedNodes.length > 0 && 
        Array.from(m.addedNodes).some(n => 
          n.nodeType === 1 && (
            n.matches?.(EDM_SELECTORS.CARDS) ||
            n.querySelector?.(EDM_SELECTORS.CARDS)
          )
        )
      );
      
      if (!hasRelevantChanges) return;
      
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => applyToAll(), 200);
    });
    
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
    
    return observer;
  }

  /**
   * 이벤트 리스너 설정 (edm-main.js와 동일)
   */
  /**
   * v8.2.4: 이벤트 핸들러 debounce 및 중복 호출 방지
   */
  const eventDebounceTimers = new Map();
  const eventProcessedFlag = new Map();
  
  function debouncedHandler(eventName, handler, delay = 100) {
    return (e) => {
      clearTimeout(eventDebounceTimers.get(eventName));
      eventDebounceTimers.set(eventName, setTimeout(() => {
        handler(e);
      }, delay));
    };
  }
  
  function setupEventListeners() {
    // v8.2.4: 중복 등록 방지
    if (window.EDM?._eventListenersSetup) return;
    
    // 이벤트 핸들러 맵 (debounce 적용)
    const eventHandlers = {
      'predictive-search:updated': debouncedHandler('search', () => applyToSearchResults(), 150),
      'recently-viewed:loaded': debouncedHandler('recently', createContainerHandler(applyDiscountsToContainer), 100),
      'product-recommendations:loaded': debouncedHandler('recommendations', createContainerHandler(applyDiscountsToContainer), 100),
      'sidebar:loaded': debouncedHandler('sidebar', createContainerHandler(applyToSidebar), 100),
      'featured-tabs:content-loaded': debouncedHandler('tabs', createContainerHandler(applyDiscountsToContainer), 150),
      // v8.2.4: popup 이벤트 통합 (동일한 핸들러 공유)
      'promotion-popup:opened': debouncedHandler('popup', () => applyDiscountsToContainer(), 100),
      'current-sales-popup:opened': debouncedHandler('popup', () => applyDiscountsToContainer(), 100),
      // v8.2.5: global.js의 PUB_SUB_EVENTS.variantChange와 일치 ('variant-change')
      'variant-change': debouncedHandler('variant', () => applyToMainProduct(), 150),
      'edm:apply': (e) => {
        if (e.detail?.container) applyDiscountsToContainer(e.detail.container);
        else applyToAll();
      }
    };

    // 이벤트 리스너 일괄 등록
    addEventListeners(eventHandlers);
    
    // 등록 완료 플래그
    window.EDM = window.EDM || {};
    window.EDM._eventListenersSetup = true;

    // ============================================
    // 비교 페이지 AJAX 로드 감지 (Wokiee 테마)
    // compare-page.js는 이벤트를 발생시키지 않으므로 MutationObserver 사용
    // ============================================
    const compareContainer = document.querySelector('.compare-ajax-content');
    if (compareContainer) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Compare page detected, setting up observer');
      
      const compareObserver = new MutationObserver((mutations) => {
        // 새 콘텐츠가 추가되었는지 확인
        const hasNewContent = mutations.some(m => 
          m.addedNodes.length > 0 && 
          [...m.addedNodes].some(n => n.nodeType === 1)
        );
        
        if (hasNewContent) {
          if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Compare AJAX content loaded');
          // DOM 업데이트 완료 대기
          setTimeout(() => processComparePage(), 100);
        }
      });
      
      compareObserver.observe(compareContainer, { 
        childList: true, 
        subtree: false 
      });
    }
    
    // compareUpdated 이벤트도 감지 (compare-page.js에서 발생)
    window.addEventListener('compareUpdated', () => {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] compareUpdated event received');
      setTimeout(() => processComparePage(), 200);
    });
  }

  /**
   * 전역 API 노출 (edm-main.js와 동일)
   */
  function exposeGlobalAPI() {
    // EDMDiscounts API (호환성)
    window.EDMDiscounts = {
      get: (id) => cache.get(id),
      has: (id) => cache.has(id),
      getMultiple: (ids) => cache.getMultiple(ids),
      getStats: () => cache.getStats(),
      dump: () => cache.dump(),
      formatPrice: PriceFormatter.formatPrice.bind(PriceFormatter),
      toCents: PriceFormatter.toCents.bind(PriceFormatter),
      applyToContainer: applyDiscountsToContainer,
      applyToSearchResults,
      applyToSidebar,
      applyToMainProduct, // 상품 페이지용
      processComparePage,  // 비교 페이지용
      applyToNotification: (notification, variantId) => {
        if (!notification || !variantId) return false;
        const discount = cache.get(variantId);
        if (!discount?.hasDiscount) return false;
        const priceEl = notification.querySelector('.cart-notification__price, [data-notification-price]');
        if (priceEl && !isElementAlreadyApplied(priceEl)) {
          priceEl.innerHTML = createDiscountPriceHTML(discount, 'notification');
          markElementAsApplied(priceEl);
          return true;
        }
        return false;
      },
      applyToAll
    };

    // EDM API (확장)
    window.EDM = window.EDM || {};
    window.EDM._userMode = true;
    window.EDM._standalone = true;
    window.EDM._version = EDM_CONSTANTS.VERSION;
    window.EDM.initialized = true;
    window.EDM.settings = EDM_SETTINGS; // v8.2: 설정 노출 (admin에서 참조)
    window.EDM._cache = cache; // v8.3.1: 캐시 객체 노출 (edm-cache-builder에서 참조)
    window.EDM.extractVariantId = extractVariantId;
    window.EDM.applyToAll = applyToAll;
    window.EDM.applyToMainProduct = applyToMainProduct; // 상품 페이지용
    window.EDM.applyToSearchResults = applyToSearchResults;
    window.EDM.applyToSidebar = applyToSidebar;
    window.EDM.applyToContainer = applyDiscountsToContainer;
    // v8.2.4: 중복 별칭 제거 - window.EDM.applyToContainer 사용 권장
    // 하위 호환성을 위해 최소한의 별칭만 유지
    window.EDM.applyToRecentlyViewed = applyDiscountsToContainer;
    window.EDM.processComparePage = processComparePage;  // 비교페이지 추가
    window.EDM.getCacheStats = () => cache.getStats();
    
    // 처리 상태 추적
    window.EDM._processedElements = new WeakSet();
    window.EDM.isElementProcessed = (el) => window.EDM._processedElements.has(el);
    window.EDM.markElementProcessed = (el) => window.EDM._processedElements.add(el);
  }

  /**
   * 메인 초기화 함수
   */
  async function init() {
    // 이미 전체 EDM이 로드된 경우 스킵
    if (window.enhancedDiscountManager?.initialized) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM] Full EDM already loaded, skipping standalone');
      return;
    }
    
    // v8.3.2: 중복 초기화 방지 (window 플래그 + 내부 변수)
    if (window.EDM_USER_INITIALIZED || initialized) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM] Already initialized, skipping duplicate initialization');
      return;
    }
    window.EDM_USER_INITIALIZED = true;
    initialized = true;
    
    // v8.2: CSS 변수 자동 주입 (theme.liquid 의존성 완전 제거)
    injectEDMCSSVariables();
    
    // CSS 변수에서 스키마 설정 추론
    finalizeSettings();
    
    if (EDM_CONSTANTS.DEBUG) console.log('[EDM] Standalone v8.3.2 initializing...');
    
    // 캐시 인스턴스 생성
    cache = new UserUnifiedCache();
    
    if (EDM_CONSTANTS.DEBUG) {
      console.log('[EDM-User] Cache created, items:', cache.memory.size);
    }
    
    // ⚠️ 중요: Gist 캐시 사전 로드 대기 (edm-main.js와 동일)
    if (cache.gistCache?.enabled) {
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Waiting for Gist cache pre-load...');
      await cache.preloadGistToMemory();
      if (EDM_CONSTANTS.DEBUG) console.log('[EDM-User] Gist cache loaded, items:', cache.memory.size);
    }
    
    // 전역 API 노출 (applyToAll에서 applyToMainProduct 사용하므로 먼저 설정)
    exposeGlobalAPI();
    
    // 페이지 카드 수 계산 (인디케이터용)
    const cards = document.querySelectorAll(EDM_SELECTORS.CARDS);
    const variantSet = new Set();
    for (const card of cards) {
      const vid = extractVariantId(card);
      if (vid) variantSet.add(vid);
    }
    const totalVariants = variantSet.size || cache.memory.size;
    
    // 로딩 인디케이터 표시
    const settings = EDM_SETTINGS.indicator;
    if (settings?.enabled && settings?.showToUsers !== false) {
      loadingIndicator = createLoadingIndicator(totalVariants);
      if (loadingIndicator) document.body.appendChild(loadingIndicator);
    }
    
    // 초기 적용
    const applied = await applyToAll();
    
    // 로딩 인디케이터 완료 (할인이 있는 variant 수 표시)
    if (loadingIndicator) {
      const finalVariants = applied.cards?.variants || totalVariants;
      // v7.3.1: discountedVariants 사용 (variant 기준 할인 수)
      const actualDiscounts = applied.cards?.discountedVariants || 0;
      updateLoadingIndicator(loadingIndicator, finalVariants, finalVariants, actualDiscounts);
      hideLoadingIndicator(loadingIndicator, settings?.autoHide ?? 2000);
      loadingIndicator = null;
    }
    
    // Observer 및 이벤트 리스너 설정
    setupObserver();
    setupEventListeners();
    
    // 초기화 완료 이벤트
    document.dispatchEvent(new CustomEvent('edm:initialized', {
      detail: { mode: 'user-standalone', cacheSize: cache.memory.size, applied: applied.total }
    }));
    
    if (EDM_CONSTANTS.DEBUG) {
      console.log('[EDM-User] Initialized:', {
        cacheSize: cache.memory.size,
        stats: cache.getStats(),
        applied
      });
    }
  }

  // DOM 준비 후 초기화
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => init(), { timeout: 3000 });
      } else {
        setTimeout(init, 100);
      }
    });
  } else {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => init(), { timeout: 2000 });
    } else {
      setTimeout(init, 50);
    }
  }

})();
